import React, { useState, useEffect } from 'react';
import { Search, X, Zap, Gift, DollarSign, Bookmark, Users, LogIn, User, Package, Printer, ChevronRight, Copy, Check, Camera, Star } from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getDatabase, ref, onValue, set } from 'firebase/database';
import BattleArena from './BattleArena';
import PokeDash from './PokeDash';
import ScrollingText from './ScrollingText';
import BorderStreak from './BorderStreak';
import AlbumArt from './AlbumArt';

// Firebase config
const firebaseConfig = {
  apiKey: "AIzaSyARv3RMmZCBVru7_sjwxNDt9HO5PxbAtx4",
  authDomain: "cheeks-live-status.firebaseapp.com",
  databaseURL: "https://cheeks-live-status-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "cheeks-live-status",
  storageBucket: "cheeks-live-status.firebasestorage.app",
  messagingSenderId: "743249460847",
  appId: "1:747249460847:web:46f2d8835c19ca8effd79a"
};

// Initialise Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);

// Only this Discord account can see/use the maintenance-mode control.
const ADMIN_DISCORD_ID = '329680758144892928';

// Special non-Pokédex character for the Card Builder, using the site's own profile picture as its sprite
const TOGECHEEKS_ENTRY = { id: 'togecheeks', name: 'togecheeks' };

// Customer reviews carousel — username and text share one index so they always change together
const REVIEWS = [
  { user: 'dmxxre_', text: "I honestly can't express how amazing this person is. They were kind enough to let a rare card go for practically nothing — I only paid postage and £1. The card arrived with a beautifully made 3D-printed Wooper keychain as a gift. Truly one of the loveliest sellers I've met." },
  { user: 'wolfsterj', text: "Fantastic seller, one of the best people on the app." },
  { user: 'tjdeku', text: "First stream back after time and you absolutely smashed it mate, quality stock, great guy — couldn't recommend you more. Looking forward to more streams in future." },
  { user: 'stormtcg', text: "Cheeeks is seriously the best! Love his vibe and enjoy his product. Shipping was pretty quick considering he's international. Everything arrived wonderfully!" },
  { user: 'taylor2704', text: "Togecheeks has the best collection of rarest gems I've seen! Amazing seller. Super quick and packaged perfect! Can't wait for your next stream!" },
  { user: 'richiebear_collects', text: "Absolutely love cheeks streams, always a blast to buy from you. Highly recommended seller." },
  { user: 'cazz944', text: "Great cards, great prices, amazing host and great vibes in stream." },
  { user: 'moose0007', text: "Vintage single and vintage pack… UK to Oklahoma… zero issues, perfect condition upon arrival. Amazing streamer and an even better person!" },
  { user: 'bthowe00', text: "Massively impressive streamer, does a lot to support others too. Check him out and give him a follow." },
  { user: 'yukocreep', text: "Fantastic job on every streaming. Love the set up, how cute and interesting to be in his show. Arigatou for the great experience!" },
  { user: 'pokemart92', text: "Awesome streamer! Great guy. Packaged perfectly and swiftly delivered. Highly recommend this cheeky chap!" },
  { user: 'lucybytes', text: "Lovely streamer, fun and great bargains to be had here." },
];

const ReviewsCarousel = () => {
  // Start on a random review each page load, then cycle through in order
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * REVIEWS.length));
  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % REVIEWS.length), 6000);
    return () => clearInterval(id);
  }, []);
  const r = REVIEWS[idx];
  // Rotating review shown over the bottom of the profile banner — stars, username and text share one key so they change together
  return (
    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'calc(100% - 24px)', maxWidth: '440px', pointerEvents: 'none', zIndex: 2 }}>
      <style>{`
        @keyframes reviewFadeUp {
          0%   { opacity: 0; transform: translateY(6px); }
          12%  { opacity: 1; transform: translateY(0); }
          85%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .review-slide { animation: reviewFadeUp 6s ease-in-out forwards; }
      `}</style>
      <div key={idx} className="review-slide" style={{ padding: '6px 10px', borderRadius: '10px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
        <div className="flex items-center gap-1.5 mb-0.5">
          <div className="flex">
            {[1,2,3,4,5].map(i => (
              <svg key={i} width="9" height="9" viewBox="0 0 24 24" fill="#FADC00"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            ))}
          </div>
          <span className="text-white text-[10px] font-bold">5.0</span>
          <span className="text-white text-[10px] font-semibold">@{r.user}</span>
        </div>
        <p style={{ color: '#ffffff', fontSize: '0.62rem', lineHeight: 1.35 }}>"{r.text}"</p>
      </div>
    </div>
  );
};

const CardExplorer = () => {
  const [isLive, setIsLive] = useState(false);
  const [memberCount, setMemberCount] = useState(null);
  const [voiceCount, setVoiceCount] = useState(0);
  const [onlineCount, setOnlineCount] = useState(null);
  const [whatnotLive, setWhatnotLive] = useState([]); // friends who are live (excludes togecheeks)
  const [whatnotSchedule, setWhatnotSchedule] = useState(null); // { date, shows } from ?wnlive schedule
  const [whatnotShop, setWhatnotShop] = useState(null); // { shopUrl, listings } kept up to date by the whatnotlive cog
  const [whatnotNextShow, setWhatnotNextShow] = useState(null); // { title, startAt, url, thumbnail }: my next scheduled stream
  const [togepiPet, setTogepiPet] = useState(null); // the server Togepi's live stats, synced by the togepi cog
  const [instagramCount, setInstagramCount] = useState(null);
  const [whatnotStats, setWhatnotStats] = useState(null); // { followers, sold, rating, reviews } from the whatnotlive cog
  const [bannerText, setBannerText] = useState('');
  const [myStatus, setMyStatus] = useState('');
  const [myPresence, setMyPresence] = useState(null);
  const [myBadges, setMyBadges] = useState([]);
  const [myActivity, setMyActivity] = useState(null);
  const [activityTitleWidth, setActivityTitleWidth] = useState(0);
  const [activityTextHeight, setActivityTextHeight] = useState(36);
  const [togeDashLeaders, setTogeDashLeaders] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [discordUser, setDiscordUser] = useState(() => {
    try {
      const saved = localStorage.getItem('discordUser');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [loginErrorVisible, setLoginErrorVisible] = useState(false);
  const [showPogoModal, setShowPogoModal] = useState(false);
  const [showHearts, setShowHearts] = useState(false);
  const [showCopiedToast, setShowCopiedToast] = useState(false);
  const [funFact, setFunFact] = useState('');

  // ── Maintenance mode ─────────────────────────────────────────────────
  // Gated on Discord OAuth only — anyone logged in as this Discord account can toggle it.
  const isAdminDiscordUser = discordUser?.id === ADMIN_DISCORD_ID;
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState('');

  useEffect(() => {
    const maintenanceRef = ref(database, 'maintenanceMode');
    const unsubscribe = onValue(maintenanceRef, (snapshot) => {
      setMaintenanceMode(snapshot.val() === true);
      setMaintenanceChecked(true);
    });
    return () => unsubscribe();
  }, []);

  const handleToggleMaintenanceMode = async () => {
    if (!isAdminDiscordUser) return;
    setMaintenanceBusy(true);
    setMaintenanceError('');
    try {
      await set(ref(database, 'maintenanceMode'), !maintenanceMode);
    } catch {
      setMaintenanceError('Update rejected — check the Firebase database rules allow this write.');
    }
    setMaintenanceBusy(false);
  };

  useEffect(() => {
    fetch('https://pokefacts.vercel.app/')
      .then(r => r.json())
      .then(d => setFunFact(d.data?.[0] || ''))
      .catch(() => {});
  }, []);

  // Badge definitions — keyed by role name, with image path and display label

  useEffect(() => {
    const t = setTimeout(() => { setShowHearts(true); }, 800);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setLoginErrorVisible(true);
    const fade = setTimeout(() => setLoginErrorVisible(false), 4500);
    const clear = setTimeout(() => setLoginError(false), 5000);
    return () => { clearTimeout(fade); clearTimeout(clear); };
  }, [loginError]);

  // Tab state — 'profile', 'creations', 'arcade', 'battle', 'builder', 'toge-dash', or 'inventory'
  // Inventory and Builder are hidden for now — flip these back to true to bring them back into the nav.
  // The pages themselves stay reachable (dormant) via their #hash even while hidden from the nav.
  const SHOW_INVENTORY_TAB = false;
  const SHOW_BUILDER_TAB = false;
  const SHOW_CREATIONS_TAB = false; // hidden from the nav to declutter it; still reachable at #creations
  const VALID_TABS = ['profile', 'creations', 'shop', 'arcade', 'battle', 'builder', 'toge-dash', ...(SHOW_INVENTORY_TAB ? ['inventory'] : [])];
  const TAB_LIST = [
    { id: 'profile',   label: 'Profile',   emoji: '🍑' },
    { id: 'shop',      label: 'Shop',      emoji: '🛍️' },
    ...(SHOW_CREATIONS_TAB ? [{ id: 'creations', label: 'Creations', emoji: '⭐' }] : []),
    ...(SHOW_BUILDER_TAB ? [{ id: 'builder', label: 'Builder', emoji: '🛠️' }] : []),
    { id: 'arcade',    label: 'Arcade',    emoji: '👾' },
    ...(SHOW_INVENTORY_TAB ? [{ id: 'inventory', label: 'Inventory', emoji: '📦' }] : []),
  ];
  const GAMES_LIST = [
    { id: 'battle',      label: "Cheeks' Battle Arena", tagline: 'Go head to head with other viewers live on stream!', banner: '/gamebanner_battle.jpg' },
    { id: 'toge-dash',   label: 'Cheeky Dash',    tagline: 'Get Togepi past the obstacles — how far can you get?', banner: '/gamebanner_togedash.jpg' },
    // Not a page: shows the server Togepi's live stats right in the Arcade (synced by the togepi cog)
    { id: 'virtual-pet', label: 'Tamagotcheeks', tagline: "Look after and help raise the server's Togepi!", petPanel: true },
  ];
  const getInitialTab = () => {
    const hash = window.location.hash.replace('#', '');
    return VALID_TABS.includes(hash) ? hash : 'profile';
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (VALID_TABS.includes(hash)) {
        setActiveTab(hash);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
    };
  }, []);
  // Once the Shop has been opened, keep it on the page (just hidden) so coming back doesn't rebuild every product photo
  const [shopOpened, setShopOpened] = useState(false);
  const [shopSearch, setShopSearch] = useState(''); // Shop search box
  useEffect(() => { if (activeTab === 'shop') setShopOpened(true); }, [activeTab]);
  const [expandedInstructions, setExpandedInstructions] = useState(null);
  const [expandedFirmware, setExpandedFirmware] = useState(null);
  // Smooth dropdowns: animate each panel's height to its measured content height, then let it size itself
  useEffect(() => {
    document.querySelectorAll('.instructions-body').forEach((el) => {
      const open = el.classList.contains('open');
      if ((el.dataset.open === '1') === open) return;
      el.dataset.open = open ? '1' : '0';
      const content = el.firstElementChild;
      // Taller panels get a little longer, so big ones don't fly open
      el.style.transitionDuration = Math.min(0.7, 0.3 + content.offsetHeight / 3000).toFixed(2) + 's';
      el.style.height = (open ? content.offsetHeight : el.getBoundingClientRect().height) + 'px';
      if (open) {
        const settle = (e) => {
          if (e.target !== el || e.propertyName !== 'height') return;
          el.removeEventListener('transitionend', settle);
          if (el.dataset.open === '1') el.style.height = 'auto';
        };
        el.addEventListener('transitionend', settle);
      } else {
        void el.offsetHeight; // lock in the current height before collapsing
        el.style.height = '0px';
      }
    });
  }, [expandedInstructions, expandedFirmware, activeTab]);
  const [firmwareState, setFirmwareState] = useState('disconnected');
  const [firmwareProgress, setFirmwareProgress] = useState(0);
  const [serialPort, setSerialPort] = useState(null);
  const [serialWriter, setSerialWriter] = useState(null);
  const [deviceVersion, setDeviceVersion] = useState(null);
  const [deviceName, setDeviceName] = useState(null);
  const LATEST_VERSION = 'v1.2.4';
  const OTA_URL = 'https://cheeks.uk/firmware/latest.bin';

  const readLines = async (reader, onLine, timeoutMs = 4000) => {
    let buf = '';
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += new TextDecoder().decode(value);
      const parts = buf.split('\n');
      buf = parts.pop();
      for (const line of parts) {
        const trimmed = line.trim();
        if (trimmed) { const stop = onLine(trimmed); if (stop) return; }
      }
    }
  };

  const connectSerial = async () => {
    if (!('serial' in navigator)) { setFirmwareState('unsupported'); return; }
    try {
      const port = await navigator.serial.requestPort({ filters: [{ usbVendorId: 0x303a }] });
      await port.open({ baudRate: 115200 });
      setFirmwareState('connecting');
      const writer = port.writable.getWriter();
      const reader = port.readable.getReader();
      await writer.write(new TextEncoder().encode('VERSION\n'));
      let ver = 'v1.0.0', name = 'Digital Display Stand';
      await readLines(reader, (line) => {
        if (line.startsWith('VERSION:')) ver = line.replace('VERSION:', '').trim();
        if (line.startsWith('NAME:')) name = line.replace('NAME:', '').trim();
        if (line === 'OK') return true;
      }, 3000);
      reader.releaseLock();
      setSerialPort(port);
      setSerialWriter(writer);
      setDeviceVersion(ver);
      setDeviceName(name);
      setFirmwareState('connected');
    } catch(e) {
      if (e.name !== 'NotFoundError') setFirmwareState('disconnected');
    }
  };

  const disconnectSerial = async () => {
    try { if (serialWriter) serialWriter.releaseLock(); } catch(e) {}
    try { if (serialPort) await serialPort.close(); } catch(e) {}
    setSerialPort(null); setSerialWriter(null);
    setDeviceVersion(null); setDeviceName(null);
    setFirmwareState('disconnected');
  };

  const runOTA = async () => {
    if (!serialPort || !serialWriter) return;
    setFirmwareState('updating');
    setFirmwareProgress(0);
    try {
      await serialWriter.write(new TextEncoder().encode(`OTA:${OTA_URL}\n`));
      const reader = serialPort.readable.getReader();
      await readLines(reader, (line) => {
        if (line.startsWith('OTA_PROGRESS:')) {
          const pct = parseInt(line.replace('OTA_PROGRESS:', ''));
          setFirmwareProgress(pct);
        } else if (line === 'OTA_DONE') {
          setFirmwareProgress(100);
          setFirmwareState('done');
          return true;
        } else if (line.startsWith('OTA_ERROR:')) {
          const msg = line.replace('OTA_ERROR:', '');
          console.error('OTA error:', msg);
          setFirmwareState('error');
          return true;
        }
      }, 120000); // 2 min timeout for the actual flash
      reader.releaseLock();
    } catch(e) {
      console.error('OTA exception:', e);
      setFirmwareState('error');
    }
  };
  const [displayImageIndex, setDisplayImageIndex] = useState(0);
  const [selectedStandImage, setSelectedStandImage] = useState('/digital_stand.png');
  const [selectedInventoryImage, setSelectedInventoryImage] = useState(null);

  // ── Card Builder state ──────────────────────────────────────────────
  // Each type carries its own matching card theme — background always follows the chosen type
  const cardTypes = [
    { id: 'Fairy',     color: '#ec4899', weak: 'Metal',     theme: { gradient: 'linear-gradient(160deg, #fce7f3 0%, #f472b6 55%, #db2777 100%)', border: '#be185d' } },
    { id: 'Grass',     color: '#4ade80', weak: 'Fire',      theme: { gradient: 'linear-gradient(160deg, #bbf7d0 0%, #22c55e 55%, #15803d 100%)', border: '#14532d' } },
    { id: 'Fire',      color: '#f97316', weak: 'Water',     theme: { gradient: 'linear-gradient(160deg, #fed7aa 0%, #f97316 55%, #b91c1c 100%)', border: '#7c2d12' } },
    { id: 'Water',     color: '#38bdf8', weak: 'Lightning', theme: { gradient: 'linear-gradient(160deg, #bae6fd 0%, #38bdf8 55%, #1d4ed8 100%)', border: '#1e3a8a' } },
    { id: 'Lightning', color: '#facc15', weak: 'Fighting',  theme: { gradient: 'linear-gradient(160deg, #fef9c3 0%, #facc15 55%, #b45309 100%)', border: '#78350f' } },
    { id: 'Psychic',   color: '#f472b6', weak: 'Darkness',  theme: { gradient: 'linear-gradient(160deg, #fbcfe8 0%, #ec4899 55%, #a21caf 100%)', border: '#701a75' } },
    { id: 'Fighting',  color: '#b45309', weak: 'Psychic',   theme: { gradient: 'linear-gradient(160deg, #fde68a 0%, #b45309 55%, #7c2d12 100%)', border: '#451a03' } },
    { id: 'Darkness',  color: '#475569', weak: 'Grass',     theme: { gradient: 'linear-gradient(160deg, #94a3b8 0%, #475569 55%, #0f172a 100%)', border: '#020617' } },
    { id: 'Metal',     color: '#94a3b8', weak: 'Fire',      theme: { gradient: 'linear-gradient(160deg, #e2e8f0 0%, #94a3b8 55%, #475569 100%)', border: '#334155' } },
    { id: 'Dragon',    color: '#818cf8', weak: 'Fairy',     theme: { gradient: 'linear-gradient(160deg, #c7d2fe 0%, #818cf8 55%, #4338ca 100%)', border: '#312e81' } },
    { id: 'Colorless', color: '#e5e7eb', weak: 'Fighting',  theme: { gradient: 'linear-gradient(160deg, #f8fafc 0%, #e2e8f0 55%, #94a3b8 100%)', border: '#64748b' } },
  ];
  // Small decorative glyph shown inside each type's energy badge — purely cosmetic
  const typeIconGlyph = (typeId) => {
    switch (typeId) {
      case 'Fire':      return <path d="M12 2.5c-.9 2.7-3.3 3.7-3.3 6.8a3.3 3.3 0 006.6 0c0-.8-.3-1.4-.6-2 1 1 1.8 2.5 1.8 4a4.5 4.5 0 01-9 0c0-3.7 2.9-5.6 4.5-8.8z"/>;
      case 'Water':     return <path d="M12 2.8s5 5.8 5 9.7a5 5 0 01-10 0c0-3.9 5-9.7 5-9.7z"/>;
      case 'Grass':     return <path d="M12 2.8c3.3 1.7 5.5 4.4 5.5 7.7a5.5 5.5 0 01-11 0c0-3.3 2.2-6 5.5-7.7z"/>;
      case 'Lightning': return <polygon points="13 2 4.5 14 10.5 14 9.5 22 19.5 9.5 12.5 9.5 12.5 2" />;
      case 'Psychic':   return <><circle cx="12" cy="12" r="4.5"/><circle cx="12" cy="12" r="7.5" fill="none" stroke="white" strokeWidth="1"/></>;
      case 'Fighting':  return <path d="M12 3l4.2 6.5H13v9.5h-2V9.5H7.8z"/>;
      case 'Darkness':  return <path d="M19.5 13.5A7.5 7.5 0 1110.5 4.5a6 6 0 009 9z"/>;
      case 'Metal':     return <rect x="6.5" y="6.5" width="11" height="11" rx="2.5"/>;
      case 'Fairy':     return <path d="M12 2.8l1.9 5.3 5.3 1.9-5.3 1.9L12 17l-1.9-5.1-5.3-1.9 5.3-1.9z"/>;
      case 'Dragon':    return <path d="M3.5 17c4.5-1 5.5-9.5 8.5-12 2 4.2 6.2 5.2 8.5 3-2 5.5-7.5 7.5-9.5 11.5-1-2-4-2.2-7.5-2.5z"/>;
      default:          return <circle cx="12" cy="12" r="4.5"/>;
    }
  };
  // Illustrated scene backgrounds — purely decorative, layered over the type-colour gradient
  const cardScenes = [
    { id: 'none',    name: 'Plain' },
    { id: 'beach',   name: 'Beach' },
    { id: 'forest',  name: 'Forest' },
    { id: 'night',   name: 'Night Sky' },
    { id: 'volcano', name: 'Volcano' },
  ];
  const sceneArt = (sceneId) => {
    switch (sceneId) {
      case 'beach':
        return (
          <svg viewBox="0 0 300 420" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="beachSky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7dd3fc" />
                <stop offset="100%" stopColor="#e0f2fe" />
              </linearGradient>
              <linearGradient id="beachSand" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fde68a" />
                <stop offset="100%" stopColor="#eab676" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="300" height="260" fill="url(#beachSky)" />
            <circle cx="228" cy="72" r="32" fill="#fef08a" />
            <rect x="0" y="230" width="300" height="190" fill="#38bdf8" />
            <path d="M0 250 Q75 230 150 250 T300 250 V420 H0 Z" fill="url(#beachSand)" />
            <path d="M0 240 Q75 225 150 240 T300 240" stroke="white" strokeOpacity="0.55" strokeWidth="4" fill="none" />
            <path d="M28 420 C28 360 34 320 40 300" stroke="#7c4a24" strokeWidth="7" fill="none" strokeLinecap="round" />
            <path d="M40 300 C20 285 10 270 5 255" stroke="#15803d" strokeWidth="8" fill="none" strokeLinecap="round" />
            <path d="M40 300 C55 280 65 262 68 248" stroke="#15803d" strokeWidth="8" fill="none" strokeLinecap="round" />
            <path d="M40 300 C42 278 48 258 58 240" stroke="#16a34a" strokeWidth="8" fill="none" strokeLinecap="round" />
            <path d="M40 300 C30 282 22 262 20 244" stroke="#16a34a" strokeWidth="8" fill="none" strokeLinecap="round" />
          </svg>
        );
      case 'forest':
        return (
          <svg viewBox="0 0 300 420" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="forestSky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#bbf7d0" />
                <stop offset="100%" stopColor="#f0fdf4" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="300" height="420" fill="url(#forestSky)" />
            <rect x="0" y="300" width="300" height="120" fill="#4d7c0f" />
            <g opacity="0.55">
              <rect x="16" y="260" width="8" height="60" fill="#65a30d" />
              <polygon points="-10,260 50,260 20,190" fill="#65a30d" />
              <rect x="166" y="260" width="8" height="60" fill="#65a30d" />
              <polygon points="140,260 200,260 170,190" fill="#65a30d" />
              <rect x="266" y="260" width="8" height="60" fill="#65a30d" />
              <polygon points="240,260 310,260 270,190" fill="#65a30d" />
            </g>
            <g>
              <rect x="50" y="280" width="10" height="90" fill="#365314" />
              <polygon points="15,290 95,290 55,200" fill="#166534" />
              <polygon points="23,250 87,250 55,170" fill="#15803d" />
              <rect x="220" y="280" width="10" height="90" fill="#365314" />
              <polygon points="185,290 265,290 225,200" fill="#166534" />
              <polygon points="193,250 257,250 225,170" fill="#15803d" />
            </g>
          </svg>
        );
      case 'night':
        return (
          <svg viewBox="0 0 300 420" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="nightSky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#1e1b4b" />
                <stop offset="100%" stopColor="#312e81" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="300" height="420" fill="url(#nightSky)" />
            {[[20,30],[60,80],[100,40],[140,110],[180,60],[220,90],[260,35],[35,150],[90,180],[150,160],[210,200],[250,140]].map(([x,y],i) => (
              <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 1.8 : 1} fill="white" opacity={0.5 + (i % 4) * 0.12} />
            ))}
            <circle cx="228" cy="80" r="28" fill="#fef9c3" />
            <circle cx="240" cy="70" r="24" fill="#1e1b4b" />
            <polygon points="0,420 40,320 80,380 130,300 180,380 230,330 300,420" fill="#000000" />
          </svg>
        );
      case 'volcano':
        return (
          <svg viewBox="0 0 300 420" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="volcanoSky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7c2d12" />
                <stop offset="60%" stopColor="#f97316" />
                <stop offset="100%" stopColor="#450a0a" />
              </linearGradient>
              <radialGradient id="volcanoGlow" cx="50%" cy="30%" r="50%">
                <stop offset="0%" stopColor="#fde047" />
                <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
              </radialGradient>
            </defs>
            <rect x="0" y="0" width="300" height="420" fill="url(#volcanoSky)" />
            <rect x="0" y="0" width="300" height="420" fill="url(#volcanoGlow)" />
            <polygon points="150,120 230,340 70,340" fill="#3f3f46" />
            <polygon points="150,120 190,220 110,220" fill="#f97316" />
            <ellipse cx="150" cy="115" rx="26" ry="10" fill="#78716c" opacity="0.6" />
            <ellipse cx="130" cy="90" rx="34" ry="12" fill="#a8a29e" opacity="0.5" />
            <rect x="0" y="330" width="300" height="90" fill="#1c1917" />
          </svg>
        );
      default:
        return null;
    }
  };
  const spriteStyles = [
    { id: 'official-artwork', name: 'Official Art', folder: 'other/official-artwork' },
    { id: 'home',             name: 'Home',         folder: 'other/home' },
    { id: 'default',          name: 'Classic',       folder: '' },
  ];
  const [builderScene, setBuilderScene] = useState('beach');
  const [builderSpriteStyle, setBuilderSpriteStyle] = useState(spriteStyles[0].id);
  const [builderPokemonList, setBuilderPokemonList] = useState([TOGECHEEKS_ENTRY]);
  const [builderPokemonSearch, setBuilderPokemonSearch] = useState('');
  const [builderShowPokemonPicker, setBuilderShowPokemonPicker] = useState(false);
  const [builderSelectedPokemon, setBuilderSelectedPokemon] = useState(TOGECHEEKS_ENTRY); // { id, name }
  const [builderCardName, setBuilderCardName] = useState('Togecheeks');
  const [builderHp, setBuilderHp] = useState('120');
  const [builderType, setBuilderType] = useState(cardTypes[0].id);
  const [builderMoveName, setBuilderMoveName] = useState('Clap');
  const [builderMoveDesc, setBuilderMoveDesc] = useState('You already know...');
  const [builderDamage, setBuilderDamage] = useState('10');
  const [builderCardNumberInput, setBuilderCardNumberInput] = useState('420/151');
  const [builderLoadCodeInput, setBuilderLoadCodeInput] = useState('');
  const [builderLoadCodeError, setBuilderLoadCodeError] = useState('');
  const [builderCodeCopied, setBuilderCodeCopied] = useState(false);

  useEffect(() => {
    fetch('https://pokeapi.co/api/v2/pokemon?limit=1025')
      .then(r => r.json())
      .then(d => {
        const list = (d.results || []).map(p => {
          const match = p.url.match(/\/pokemon\/(\d+)\//);
          return { name: p.name, id: match ? parseInt(match[1], 10) : null };
        }).filter(p => p.id);
        setBuilderPokemonList([TOGECHEEKS_ENTRY, ...list]);
      })
      .catch(() => {});
  }, []);

  const pokemonSpriteUrl = (id, styleId = builderSpriteStyle) => {
    if (id === 'togecheeks') return '/pfp.png';
    const style = spriteStyles.find(s => s.id === styleId) || spriteStyles[0];
    const path = style.folder ? `${style.folder}/${id}.png` : `${id}.png`;
    return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${path}`;
  };

  const filteredBuilderPokemon = builderPokemonSearch.trim()
    ? builderPokemonList.filter(p => p.name.includes(builderPokemonSearch.trim().toLowerCase())).slice(0, 30)
    : [];

  // Compact code format: index|index|id|hp|name|moveName|moveDesc, joined with a control
  // character (never typed by users) then base64url'd. No pokemonName or full type/bg
  // strings — those are derived back from their index/id on decode, keeping the code short.
  const CODE_SEP = '';

  const encodeCardCode = ({ pokemonId, typeIndex, sceneIndex, spriteIndex, name, hp, moveName, moveDesc, damage, cardNumber }) => {
    const payload = [pokemonId, typeIndex, sceneIndex, spriteIndex, hp, name, moveName, moveDesc, damage, cardNumber].join(CODE_SEP);
    return btoa(unescape(encodeURIComponent(payload)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const decodeCardCode = (code) => {
    try {
      let b64 = code.trim().replace(/-/g, '+').replace(/_/g, '/');
      b64 += '='.repeat((4 - (b64.length % 4)) % 4);
      const payload = decodeURIComponent(escape(atob(b64)));
      const [pokemonId, typeIndex, sceneIndex, spriteIndex, hp, name, moveName, moveDesc, damage, cardNumber] = payload.split(CODE_SEP);
      const type = cardTypes[parseInt(typeIndex, 10)];
      const scene = cardScenes[parseInt(sceneIndex, 10)];
      const sprite = spriteStyles[parseInt(spriteIndex, 10)];
      if (!type || !scene || !sprite) return null;
      // pokemonId is usually a Pokédex number, but the special Togecheeks entry uses a non-numeric id
      const parsedPokemonId = pokemonId === 'togecheeks' ? 'togecheeks' : parseInt(pokemonId, 10);
      return { pokemonId: parsedPokemonId, typeId: type.id, sceneId: scene.id, spriteId: sprite.id, hp, name, moveName, moveDesc, damage: damage || '', cardNumber: cardNumber || '' };
    } catch {
      return null;
    }
  };

  const builderCode = (builderSelectedPokemon && builderCardName.trim())
    ? encodeCardCode({
        pokemonId: builderSelectedPokemon.id,
        typeIndex: cardTypes.findIndex(t => t.id === builderType),
        sceneIndex: cardScenes.findIndex(s => s.id === builderScene),
        spriteIndex: spriteStyles.findIndex(s => s.id === builderSpriteStyle),
        name: builderCardName.trim(),
        hp: builderHp,
        moveName: builderMoveName,
        moveDesc: builderMoveDesc,
        damage: builderDamage.trim(),
        cardNumber: builderCardNumberInput.trim(),
      })
    : '';

  const handleCopyBuilderCode = () => {
    if (!builderCode) return;
    navigator.clipboard.writeText(builderCode).then(() => {
      setBuilderCodeCopied(true);
      setTimeout(() => setBuilderCodeCopied(false), 1800);
    }).catch(() => {});
  };

  const handleLoadBuilderCode = async () => {
    const decoded = decodeCardCode(builderLoadCodeInput);
    if (!decoded) {
      setBuilderLoadCodeError('That code doesn\'t look right — double check and try again.');
      return;
    }
    setBuilderLoadCodeError('');
    setBuilderCardName(decoded.name);
    setBuilderHp(decoded.hp);
    setBuilderType(decoded.typeId);
    setBuilderScene(decoded.sceneId);
    setBuilderSpriteStyle(decoded.spriteId);
    setBuilderMoveName(decoded.moveName);
    setBuilderMoveDesc(decoded.moveDesc);
    setBuilderDamage(decoded.damage);
    setBuilderCardNumberInput(decoded.cardNumber);
    setBuilderLoadCodeInput('');

    const known = builderPokemonList.find(p => p.id === decoded.pokemonId);
    if (known || decoded.pokemonId === 'togecheeks') {
      setBuilderSelectedPokemon(known || TOGECHEEKS_ENTRY);
      return;
    }
    try {
      const r = await fetch(`https://pokeapi.co/api/v2/pokemon/${decoded.pokemonId}`);
      const d = await r.json();
      setBuilderSelectedPokemon({ id: decoded.pokemonId, name: d.name });
    } catch {
      setBuilderSelectedPokemon({ id: decoded.pokemonId, name: `pokemon-${decoded.pokemonId}` });
    }
  };

  const selectedBuilderType = cardTypes.find(t => t.id === builderType) || cardTypes[0];
  const selectedBuilderBackground = selectedBuilderType.theme;
  // Sensible auto defaults — shown unless the user types their own override below
  const builderAutoDamage = builderHp ? Math.max(10, Math.round((parseInt(builderHp, 10) * 0.7) / 10) * 10) : 30;
  const builderAutoCardNumber = (builderSelectedPokemon && Number.isFinite(builderSelectedPokemon.id))
    ? `${String(builderSelectedPokemon.id).padStart(3, '0')}/151`
    : '000/151';
  const builderDisplayDamage = builderDamage.trim() || String(builderAutoDamage);
  const builderDisplayCardNumber = builderCardNumberInput.trim() || builderAutoCardNumber;

  // Cursor-tracked tilt + holo sheen for the live preview card
  const [builderTilt, setBuilderTilt] = useState({ rx: 0, ry: 0, mx: 50, my: 50, active: false });
  const handleBuilderCardMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    setBuilderTilt({ rx: (0.5 - py) * 16, ry: (px - 0.5) * 16, mx: px * 100, my: py * 100, active: true });
  };
  const handleBuilderCardMouseLeave = () => {
    setBuilderTilt({ rx: 0, ry: 0, mx: 50, my: 50, active: false });
  };

  useEffect(() => {
    if (!builderShowPokemonPicker) return;
    const handler = (e) => { if (!e.target.closest('[data-pokemon-picker]')) setBuilderShowPokemonPicker(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [builderShowPokemonPicker]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayImageIndex(i => (i + 1) % 5);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (window.location.hash.replace('#', '') !== tab) {
      window.history.pushState({ tab }, '', '#' + tab);
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  const bannerBubbleRef = React.useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [images, setImages] = useState([]);

  const [currentExampleIndex, setCurrentExampleIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  const examples = ['Pikachu', 'Giratina', 'Sunflora'];

  const categories = [
    { id: 'all', name: 'All Items', folder: null },
    { id: 'tcg', name: 'Pokemon TCG', folder: 'TCG' },
    { id: 'nontcg', name: 'Non-TCG', folder: 'nonTCG' },
  ];

  const handleDiscordLogin = () => {
    const CLIENT_ID = '1360587565190746193';
    const REDIRECT_URI = encodeURIComponent(window.location.origin);
    const SCOPE = encodeURIComponent('identify guilds guilds.members.read');
    const AUTH_URL = `https://discord.com/api/oauth2/authorize?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=token&scope=${SCOPE}`;
    window.location.href = AUTH_URL;
  };

  const handleLogout = () => {
    localStorage.removeItem('discordUser');
    setDiscordUser(null);
    setShowUserMenu(false);
  };

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => {
      if (!e.target.closest('[data-user-menu]')) setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showUserMenu]);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      const params = new URLSearchParams(hash.replace('#', '?'));
      const token = params.get('access_token');
      window.history.replaceState({}, document.title, window.location.pathname);
      fetch('https://discord.com/api/users/@me', {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => res.json())
        .then(userData => {
          return fetch('https://discord.com/api/users/@me/guilds', {
            headers: { Authorization: `Bearer ${token}` }
          })
            .then(res => res.json())
            .then(guilds => {
              const isMember = guilds.some(g => g.id === '1466528743265931459');
              if (isMember) {
                return fetch('https://discord.com/api/users/@me/guilds/1466528743265931459/member', {
                  headers: { Authorization: `Bearer ${token}` }
                })
                  .then(res => res.ok ? res.json() : null)
                  .then(member => {
                    const finalUser = { ...userData, nickname: member?.nick || userData.global_name || userData.username };
                    localStorage.setItem('discordUser', JSON.stringify(finalUser));
                    setDiscordUser(finalUser);
                  });
              } else {
                setLoginError(true);
              }
            });
        })
        .catch(err => console.error("Login failed:", err));
    }
  }, []);

  useEffect(() => {
    const liveRef = ref(database, 'isLive');
    const unsubscribe = onValue(liveRef, (snapshot) => setIsLive(snapshot.val() === true));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const memberRef = ref(database, 'memberCount');
    const unsubscribe = onValue(memberRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setMemberCount(value);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const voiceRef = ref(database, 'voiceCount');
    const unsubscribe = onValue(voiceRef, (snapshot) => setVoiceCount(snapshot.val() || 0));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const onlineRef = ref(database, 'onlineCount');
    const unsubscribe = onValue(onlineRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setOnlineCount(value);
    });
    return () => unsubscribe();
  }, []);

  // Whatnot sellers who are live right now (written by the whatnotlive cog)
  useEffect(() => {
    const liveRef = ref(database, 'whatnotLive');
    const unsubscribe = onValue(liveRef, (snapshot) => {
      const value = snapshot.val();
      const list = Array.isArray(value) ? value.filter(Boolean) : Object.values(value || {});
      // Our own live status already shows on the Whatnot card, so the banner is just friends
      setWhatnotLive(list.filter(s => (s.username || '').toLowerCase() !== 'togecheeks'));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const igRef = ref(database, 'instagramCount');
    const unsubscribe = onValue(igRef, (snapshot) => {
      const value = snapshot.val();
      if (value !== null && value !== undefined) setInstagramCount(value);
    });
    return () => unsubscribe();
  }, []);

  // The server Togepi (Tamagotcheeks in the Arcade): stats, mood, level and evolution, from the togepi cog
  useEffect(() => {
    const petRef = ref(database, 'togepi');
    const unsubscribe = onValue(petRef, (snapshot) => {
      const value = snapshot.val();
      setTogepiPet(value && typeof value === 'object' ? value : null);
    });
    return () => unsubscribe();
  }, []);

  // My next scheduled Whatnot stream, for the "Next stream" panel (kept up to date by the whatnotlive cog)
  useEffect(() => {
    const nextRef = ref(database, 'whatnotNextShow');
    const unsubscribe = onValue(nextRef, (snapshot) => {
      const value = snapshot.val();
      setWhatnotNextShow(value && typeof value === 'object' && value.startAt ? value : null);
    });
    return () => unsubscribe();
  }, []);

  // Your Whatnot shop listings for the Shop tab, refreshed every 15 minutes by the whatnotlive cog
  useEffect(() => {
    const shopRef = ref(database, 'whatnotShop');
    const unsubscribe = onValue(shopRef, (snapshot) => {
      const value = snapshot.val();
      if (!value || typeof value !== 'object') { setWhatnotShop(null); return; }
      const listings = Array.isArray(value.listings) ? value.listings.filter(Boolean) : Object.values(value.listings || {});
      setWhatnotShop({ shopUrl: value.shopUrl, listings });
    });
    return () => unsubscribe();
  }, []);

  // Today's upcoming Whatnot shows, published when ?wnlive schedule is run
  useEffect(() => {
    const scheduleRef = ref(database, 'whatnotSchedule');
    const unsubscribe = onValue(scheduleRef, (snapshot) => {
      const value = snapshot.val();
      if (!value || typeof value !== 'object') { setWhatnotSchedule(null); return; }
      const shows = Array.isArray(value.shows) ? value.shows.filter(Boolean) : Object.values(value.shows || {});
      setWhatnotSchedule({ date: value.date, shows });
    });
    return () => unsubscribe();
  }, []);

  // Whatnot profile stats, refreshed every 6 hours by the whatnotlive cog
  useEffect(() => {
    const wnRef = ref(database, 'whatnotStats');
    const unsubscribe = onValue(wnRef, (snapshot) => {
      const value = snapshot.val();
      if (value && typeof value === 'object') setWhatnotStats(value);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const lbRef = ref(database, 'pokeDashLeaderboard');
    const unsubscribe = onValue(lbRef, (snapshot) => {
      const data = snapshot.val() || {};
      const list = Object.values(data).sort((a, b) => b.score - a.score).slice(0, 3);
      setTogeDashLeaders(list);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const bannerRef = ref(database, 'bannerText');
    const unsubscribe = onValue(bannerRef, (snapshot) => setBannerText(snapshot.val() || ''));
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const statusRef = ref(database, 'myStatus');
    const unsubscribe = onValue(statusRef, (snapshot) => {
      setMyStatus(snapshot.val() || '');
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const presenceRef = ref(database, 'myPresence');
    const unsubscribe = onValue(presenceRef, (snapshot) => {
      setMyPresence(snapshot.val() || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const badgesRef = ref(database, 'myBadges');
    const unsubscribe = onValue(badgesRef, (snapshot) => {
      setMyBadges(snapshot.val() || []);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const activityRef = ref(database, 'myActivity');
    const unsubscribe = onValue(activityRef, (snapshot) => {
      setMyActivity(snapshot.val() || null);
    });
    return () => unsubscribe();
  }, []);


  const [elapsed, setElapsed] = useState('');
  useEffect(() => {
    if (!myActivity?.startedAt) { setElapsed(''); return; }
    const tick = () => {
      const secs = Math.floor((Date.now() - myActivity.startedAt) / 1000);
      const h = Math.floor(secs / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const sc = secs % 60;
      setElapsed(h > 0
        ? `${h}:${String(m).padStart(2,'0')}:${String(sc).padStart(2,'0')}`
        : `${m}:${String(sc).padStart(2,'0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [myActivity?.startedAt]);

  const formatCount = (count) => {
    if (!count) return null;
    return count.toLocaleString();
  };

  useEffect(() => {
    // TCG Images
const tcgFileList = [
  'Articuno Holo - Fossil - Japanese.png',
  'Blissey Holo - Neo Revelation - Japanese.png',
  'Charizard EX SIR - Pokemon 151 - English.png',
  'Charizard Rainbow - Brilliant Stars - English.png',
  'Charizard Rev Holo - Premium File Promo - Japanese.png',
  'Dark Raichu Holo - Team Rocket WOTC - English.png',
  'Eevee EX Promo - Prismatic Evolutions UPC - English.png',
  'Eevee EX SAR - Terastal Festival - Japanese.png',
  'Entei Rev Holo - Premium File Promo - Japanese.png',
  'Felt Hat Pikachu - Promo - English.png',
  'Gengar VMAX Alt Art 1 - Fusion Strike - English.png',
  'Gengar VMAX Alt Art 2 - Fusion Strike - English.png',
  'Gengar VMAX Alt Art 3 - Fusion Strike - English.png',
  'Giratina V Alt Art - Lost Origin - English.png',
  'Haunter Holo - Fossil - Japanese.png',
  'Lugia Holo - Neo Genesis - Japanese.png',
  'Lugia V Alt Art - Silver Tempest - English.png',
  'Mega Charizard EX - Inferno X - Japanese.png',
  'Mega Charizard EX Promo 1 - Charizard UPC - English.png',
  'Mega Charizard EX Promo 2 - Charizard UPC - English.png',
  'Mega Charizard EX SIR - Phantasmal Flames - English.png',
  'Mew EX - Pokemon 151 - Japanese.png',
  'Mew Holo - WOTC Promo - English.png',
  'Moltres Holo - Fossil - Japanese.png',
  'Moltres V Alt Art - Chilling Reign - English.png',
  'Pichu Rev Holo - Premium File Promo - Japanese.png',
  'Rayquaza VMAX 1 - Silver Tempest - English.png',
  'Rayquaza VMAX 2 - Silver Tempest - English.png',
  'Spiky Eared Pichu Holo - Legend of Arceus Promo - Japanese.png',
  'Togepi Rev Holo - Southern Islands Promo - Japanese.png',
  'Umbreon VMAX Alt Art - Evolving Skies - English.png',
];

    // Non-TCG Images (plushies, figures, sealed products, etc.)
const nonTCGFileList = [
  'Batomen - Series 1 - TOMY.jpg',
  'Batomen - Series 2 - TOMY.jpg',
  'Batomen - Series 3 - TOMY.jpg',
  'Battle Memo - Part 1 - TOMY.jpg',
  'Battle Memo - Part 2 - TOMY.jpg',
  'Battle Memo - Part 3 - TOMY.jpg',
  'Blastoise  - Batomen - TOMY.jpg',
  'Charizard - Batomen - TOMY.jpg',
  'Charmander - Topsun Blue Back - Japanese.png',
  'Coonuts - Series 3 - Bandai.jpg',
  'Gengar Clear Promo - PokeMaze - Pokemon Center Japan.jpg',
  'Mew Holo - Batomen - TOMY.jpg',
  'Mewtwo Holo - Batomen - TOMY.jpg',
  'Pikachu - Pixel Coaster - Pokemon Center Japan.jpg',
  'Pikachu - Pokepeace - Plushy Notepad.jpg',
  'Pikachu & Eevee - PokeGET.jpg',
  'Snorlax - Bandai Anime - Carddass.jpg',
  'Umbreon Clear Promo - PokeMaze - Pokemon Center Japan.jpg',
  'Venusaur  - Batomen - TOMY.jpg',
  'Wilfried Zaha & Yannick Bolasie - Crystal Palace Dual Auto (9 of 10) - Topps Chrome Premier League 2026.png',
];

    const tcgImages = tcgFileList.map((filename, index) => {
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      let parts = nameWithoutExt.split('-').map(p => p.trim()).filter(p => p);
      let gradeLabel = null;
      const gradingCompanies = ['PSA', 'BGS', 'ACE', 'CGC'];
      const firstPart = parts[0];
      const weightMatch = firstPart.match(/^(\d+\.?\d*g)\s+(.+)/i);
      if (weightMatch) {
        gradeLabel = weightMatch[1];
        parts[0] = weightMatch[2];
      } else {
        for (const company of gradingCompanies) {
          if (firstPart.toUpperCase().startsWith(company)) {
            const gradeMatch = firstPart.match(new RegExp(`${company}\\s*([0-9.]+)\\s*(.*)`, 'i'));
            if (gradeMatch) {
              gradeLabel = `${company} ${gradeMatch[1]}`;
              parts[0] = gradeMatch[2].trim();
              if (!parts[0]) parts.shift();
            }
            break;
          }
        }
      }
      const cardName = parts[0] || 'Unknown Card';
      const setLines = parts.slice(1);
      let serialLabel = null;
      const serialMatch = nameWithoutExt.match(/\((\d+)\s+of\s+(\d+)\)/i);
      if (serialMatch) {
        const num = String(parseInt(serialMatch[1])).padStart(2, '0');
        const total = String(parseInt(serialMatch[2])).padStart(2, '0');
        serialLabel = `${num} / ${total}`;
      }
      return { id: index + 1, name: cardName, set: setLines.join('  '), filename, category: 'tcg', grade: gradeLabel, serial: serialLabel, folder: 'TCG' };
    });

    const nonTCGImages = nonTCGFileList.map((filename, index) => {
      const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
      let parts = nameWithoutExt.split('-').map(p => p.trim()).filter(p => p);
      let gradeLabel = null;
      const firstPart = parts[0];
      const weightMatch = firstPart.match(/^(\d+\.?\d*g)\s+(.+)/i);
      if (weightMatch) {
        gradeLabel = weightMatch[1];
        parts[0] = weightMatch[2];
      }
      // Extract serial number e.g. (9 of 10) or (09 of 10) from any part
      let serialLabel = null;
      const fullName = nameWithoutExt;
      const serialMatch = fullName.match(/\((\d+)\s+of\s+(\d+)\)/i);
      if (serialMatch) {
        const num = String(parseInt(serialMatch[1])).padStart(2, '0');
        const total = String(parseInt(serialMatch[2])).padStart(2, '0');
        serialLabel = `${num} / ${total}`;
      }
      const itemName = parts[0] || 'Unknown Item';
      const itemLines = parts.slice(1);
      return { id: tcgImages.length + index + 1, name: itemName, set: itemLines.join('  '), filename, category: 'nontcg', grade: gradeLabel, serial: serialLabel, folder: 'nonTCG' };
    });

    setImages([...tcgImages, ...nonTCGImages]);
  }, []);

  useEffect(() => {
    const currentExample = examples[currentExampleIndex];
    if (isTyping) {
      if (currentText.length < currentExample.length) {
        const timeout = setTimeout(() => setCurrentText(currentExample.substring(0, currentText.length + 1)), 100);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => setIsTyping(false), 2000);
        return () => clearTimeout(timeout);
      }
    } else {
      if (currentText.length > 0) {
        const timeout = setTimeout(() => setCurrentText(currentText.substring(0, currentText.length - 1)), 50);
        return () => clearTimeout(timeout);
      } else {
        const timeout = setTimeout(() => {
          setCurrentExampleIndex((prevIndex) => (prevIndex + 1) % examples.length);
          setIsTyping(true);
        }, 500);
        return () => clearTimeout(timeout);
      }
    }
  }, [currentText, isTyping, currentExampleIndex]);

  useEffect(() => {
    const video = document.createElement('video');
    video.src = '/splash.mp4';
    video.preload = 'auto';
    video.load();
  }, []);

  // Prevent right-click / long-press saving of images
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      img { -webkit-user-select: none; user-select: none; -webkit-touch-callout: none; pointer-events: none; }
      a img, button img { pointer-events: auto; }
      * { -webkit-touch-callout: none; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (activeTab !== 'profile') return;
    const container = bannerBubbleRef.current;
    if (!container) return;
    const bubbles = [];
    for (let i = 0; i < 22; i++) {
      const b = document.createElement('div');
      b.className = 'banner-bubble';
      const size = Math.random() * 20 + 12;
      b.style.width = `${size}px`;
      b.style.height = `${size}px`;
      b.style.left = `${Math.random() * 100}%`;
      b.style.animationDelay = `-${Math.random() * 8}s`;
      b.style.animationDuration = `${Math.random() * 4 + 5}s`;
      b.style.setProperty('--bdrift', Math.random() * 2 - 1);
      container.appendChild(b);
      bubbles.push(b);
    }
    return () => bubbles.forEach(b => b.remove());
  }, [activeTab, maintenanceChecked, maintenanceMode]);

  const handleSearch = () => { /* filter is reactive */ };

  const filteredImages = images.filter(img => {
    if (!searchQuery) return selectedCategory === 'all' || img.category === selectedCategory;
    const searchWords = searchQuery.toLowerCase().trim().split(/\s+/);
    const searchableText = [img.name, img.set, img.filename, img.grade || ''].join(' ').toLowerCase();
    const matchesSearch = searchWords.every(word => searchableText.includes(word));
    const matchesCategory = selectedCategory === 'all' || img.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // ── Profile link data ──────────────────────────────────────────────
  const profileLinks = [
    {
      label: 'Discord',
      sub: 'discord.cheeks.uk',
      href: 'https://cheeks.uk/docs',
      color: '#5865F2',
      bgColor: 'rgba(88,101,242,0.12)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057c.001.022.015.043.032.054a19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/>
        </svg>
      )
    },
    {
      label: 'Instagram',
      sub: '@togecheeks',
      href: 'https://www.instagram.com/togecheeks',
      color: '#E1306C',
      bgColor: 'rgba(225,48,108,0.12)',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      )
    },
    {
      label: 'Whatnot — Live Auctions',
      sub: 'Watch & bid live',
      href: 'https://www.whatnot.com/user/togecheeks/shows',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      isButton: true,
      icon: <Gift className="h-5 w-5" />
    },
    {
      label: 'Get £10 FREE on Whatnot',
      sub: 'New buyer promo',
      href: 'https://whatnot.com/invite/togecheeks',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      icon: <DollarSign className="h-5 w-5" />
    },
    {
      label: 'Bookmark Next Livestreams',
      sub: 'Never miss a drop',
      href: 'https://www.whatnot.com/user/togecheeks/shows',
      color: '#FADC00',
      bgColor: 'rgba(250,220,0,0.1)',
      icon: <Bookmark className="h-5 w-5" />
    },
  ];

  const badgeMap = {
    diamond: { img: '/diamond.png', label: 'Diamond' },
  };

  const badges = (Array.isArray(myBadges) ? myBadges : Object.keys(myBadges || {}))
    .map(key => badgeMap[key])
    .filter(Boolean);

  // Don't paint the real site (or the maintenance page) until we know which one to show —
  // avoids a flash of the live site before the maintenance check comes back from Firebase.
  if (!maintenanceChecked) {
    return <div className="bg-black" style={{ minHeight: '100svh' }} />;
  }

  // Styles for the stand panel (collapsible dropdowns, card animations). Shared by Creations and Profile.
  const STAND_PANEL_CSS = `
    @keyframes creationFadeUp {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes bellRing {
    0%,100% { transform: rotate(0deg); }
    10%     { transform: rotate(14deg); }
    20%     { transform: rotate(-12deg); }
    30%     { transform: rotate(10deg); }
    40%     { transform: rotate(-8deg); }
    50%     { transform: rotate(0deg); }
    }
    .creation-card {
    animation: creationFadeUp 0.45s ease both;
    }
    /* Height is animated from JS (see the dropdown effect in CardExplorer) using the content's real height */
    .instructions-body {
    height: 0;
    overflow: hidden;
    transition: height 0.4s cubic-bezier(0.33, 1, 0.68, 1);
    }
    .instructions-inner {
    opacity: 0;
    transform: translateY(-6px);
    transition: opacity 0.25s ease, transform 0.4s cubic-bezier(0.33, 1, 0.68, 1);
    }
    .instructions-body.open > .instructions-inner {
    opacity: 1;
    transform: none;
    transition-delay: 0.05s;
    }
    @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
    }
    .instructions-shimmer {
    background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 55%, transparent 100%);
    background-size: 200% auto;
    animation: shimmer 4s ease-in-out infinite;
    }
    .epaper-img {
    filter: grayscale(100%) contrast(1.4) brightness(1.1);
    image-rendering: pixelated;
    }
  `;

  // Digital Display Stand V2: the same panel on Creations and on the Profile page
  const DIGITAL_STAND_V2 = {
    id: 1,
    name: 'Digital Display Card & Slab Stand V2',
    tag: '3D Printed',
    badge: 'Digital + Physical',
    description: 'A wirelessly controlled display stand, designed for graded slabs and raw cards from all major TCGs — Pokémon, MTG, Yu-Gi-Oh!, Disney, Sports and more. Change what\'s shown on screen from any device on your Wi-Fi: custom graphics, shout-outs, offers, discount codes, or stream overlays. Stream Deck compatible. The screen holds its image without power — and is only needed when updating the display.',
    price: '£79.99',
    availability: 'Available',
    // Setup, kept as simple as an app: each step has an icon (drawn in renderStandV2)
    steps: [
      { icon: 'download', title: 'Get the app', detail: 'Download the Digital Display app from the App Store or Google Play.' },
      { icon: 'power',    title: 'Plug it in',  detail: 'Connect your stand to power with the USB cable. It turns on in a few seconds.' },
      { icon: 'wifi',     title: 'Add your stand', detail: 'Open the app and tap Add device. Your stand appears nearby. Pick your Wi-Fi, enter the password, done.' },
      { icon: 'send',     title: 'Show anything', detail: 'Pick a stream graphic, a shout-out, special offer, discount code or whatever you like and tap Send. Your display updates in seconds.' },
    ]
  };
  const renderStandV2 = (item, { showImage = true, anchorId = 'digital-stand-card' } = {}) => (
    <div key={item.id} id={anchorId} className="creation-card bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] overflow-hidden">
      <style>{STAND_PANEL_CSS}</style>
      {showImage && (<>
        {/* Product image */}
        <div className="relative w-full overflow-hidden glow-neutral" style={{aspectRatio:'4/3', background:'radial-gradient(ellipse at 50% 55%, rgba(4,175,247,0.07) 0%, rgba(244,42,228,0.05) 40%, rgba(0,0,0,0.0) 75%)'}}>
          {/* Orb glow — slow rotating sweep instead of pulsing blobs */}
          <style>{`
            @keyframes glowSweep {
              0%   { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .center-glow-spin { animation: glowSweep 14s linear infinite; will-change: transform; }
          `}</style>
          <div className="center-glow-spin absolute" style={{top:'-35%',left:'-35%',right:'-35%',bottom:'-35%',background:'conic-gradient(from 0deg, rgba(4,175,247,0.28), rgba(244,42,228,0.28), rgba(4,175,247,0.28))',filter:'blur(45px)',zIndex:1,transformOrigin:'50% 50%'}} />
          {/* Circuit SVG — grid only */}
          <svg style={{position:'absolute',inset:0,width:'100%',height:'100%',zIndex:2}} viewBox="0 0 600 450" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <defs>
              <pattern id="cgrid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(4,175,247,0.18)" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="600" height="450" fill="url(#cgrid)"/>
          </svg>

          <img
            src={selectedStandImage}
            alt={item.name}
            className="w-full h-full object-contain px-8"
            style={{position:'relative', zIndex:2}}
          />
          {/* Gallery prev/next arrows */}
          <button
            onClick={() => {
              const imgs = ['/digital_stand.png', '/digital_stand_rear.png', '/digital_stand_side.png'];
              const i = imgs.indexOf(selectedStandImage);
              setSelectedStandImage(imgs[(i - 1 + imgs.length) % imgs.length]);
            }}
            aria-label="Previous image"
            className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full transition-all duration-200 active:scale-90"
            style={{width:'28px', height:'28px', background:'rgba(0,0,0,0.55)', border:'1px solid rgba(255,255,255,0.15)', zIndex:30}}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <button
            onClick={() => {
              const imgs = ['/digital_stand.png', '/digital_stand_rear.png', '/digital_stand_side.png'];
              const i = imgs.indexOf(selectedStandImage);
              setSelectedStandImage(imgs[(i + 1) % imgs.length]);
            }}
            aria-label="Next image"
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center rounded-full transition-all duration-200 active:scale-90"
            style={{width:'28px', height:'28px', background:'rgba(0,0,0,0.55)', border:'1px solid rgba(255,255,255,0.15)', zIndex:30}}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          {/* New Improved Design pill — top right */}
          <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #04aff7, #f42ae4)', zIndex:30}}>
            New Improved Design
          </div>
          {/* Mini cycling display — top left */}
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5" style={{zIndex:30}}>
            <div className="relative rounded overflow-hidden" style={{width:'99px', height:'43px', background:'#000000'}}>
              {[1,2,3,4,5].map((n) => (
                <img
                  key={n}
                  src={`/display${n}.png`}
                  alt={`Display example ${n}`}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    opacity: displayImageIndex === n - 1 ? 1 : 0,
                    filter: 'url(#epaper)',
                    transition: 'opacity 0.7s ease',
                  }}
                />
              ))}
            </div>
            <div className="w-full text-center px-2 py-0.5 rounded-full text-[9px] font-semibold text-white/70" style={{background:'rgba(0,0,0,0.75)'}}>
              Example content
            </div>
          </div>
          {/* V2 version badge — bottom left */}
          <div className="absolute bottom-3 left-3 flex items-center justify-center rounded-full text-white font-bold text-[11px]" style={{width:'28px', height:'28px', background:'linear-gradient(135deg,#04aff7,#f42ae4)', boxShadow:'0 0 8px rgba(4,175,247,0.4)', zIndex:30}}>
            V2
          </div>
          {/* Thumbnail gallery — bottom right */}
          <div className="absolute bottom-3 right-3 flex items-center gap-2" style={{zIndex:30}}>
            {['/digital_stand.png', '/digital_stand_rear.png', '/digital_stand_side.png'].map((src, i) => (
              <button
                key={i}
                onClick={() => setSelectedStandImage(src)}
                className="rounded-lg overflow-hidden transition-all duration-200"
                style={{
                  width: '44px',
                  height: '44px',
                  background: '#000000',
                  border: selectedStandImage === src ? '2px solid #04aff7' : '2px solid rgba(255,255,255,0.15)',
                  boxShadow: selectedStandImage === src ? '0 0 8px rgba(4,175,247,0.4)' : 'none',
                }}
              >
                <img src={src} alt={`View ${i + 1}`} className="w-full h-full object-contain p-1" />
              </button>
            ))}
          </div>

          {/* Per-image labels */}
          <style>{`
            @keyframes labelPop {
              0%   { opacity:0; transform: scale(0.8) translateY(4px); }
              60%  { transform: scale(1.05) translateY(-1px); }
              100% { opacity:1; transform: scale(1) translateY(0); }
            }
            .img-label { animation: labelPop 0.3s cubic-bezier(0.34,1.56,0.64,1) both; }
          `}</style>
          {({
            '/digital_stand.png': [
              { text: '2.9" Glass Display', style: { top: '22%', right: '6%' } },
              { text: 'Display Without Power', style: { bottom: '22%', left: '8%' } },
            ],
            '/digital_stand_rear.png': [
              { text: 'USB-C Port', style: { bottom: '22%', left: '8%' } },
              { text: 'WiFi + Bluetooth', style: { top: '28%', right: '6%' } },
            ],
            '/digital_stand_side.png': [
              { text: 'Precision Designed', style: { top: '22%', right: '6%' } },
              { text: 'Impossibly Compact', style: { bottom: '22%', left: '6%' } },
            ],
          }[selectedStandImage] || []).map((label, i) => (
            <div
              key={`${selectedStandImage}-${i}`}
              className="img-label absolute flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white font-semibold pointer-events-none"
              style={{
                fontSize: '0.75rem',
                background: 'rgba(0,0,0,0.82)',
                border: '1px solid rgba(255,255,255,0.15)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                zIndex: 4,
                animationDelay: `${i * 0.08}s`,
                ...label.style,
              }}
            >
              <span style={{width:'6px',height:'6px',borderRadius:'50%',background:'rgba(4,175,247,0.8)',flexShrink:0,display:'inline-block'}} />
              {label.text}
            </div>
          ))}
        </div>

      </>)}
      {/* Info block */}
      <div className="px-4 pt-4 pb-3 relative edge-glow-top-thin edge-glow-bold">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h2 className="text-white font-bold text-base leading-tight">{item.name}</h2>
          <button
            type="button"
            onClick={() => { switchTab('shop'); window.scrollTo({ top: 0 }); }}
            className="shrink-0 rounded-full text-white font-extrabold uppercase transition-transform active:scale-95 hover:scale-105"
            style={{
              padding: '5px 12px',
              fontSize: '0.82rem',
              lineHeight: 1.2,
              letterSpacing: '0.04em',
              background: 'linear-gradient(90deg, #ec4899, #8b5cf6)',
              boxShadow: '0 2px 10px rgba(236,72,153,0.35)',
            }}
          >
            Buy Now
          </button>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          {[
            '4 Colour Greyscale',
            'Wireless Control',
            'Low Power',
            'Stream Deck Ready',
            'Easy Setup',
            'No App Required',
            'Plug & Play',
            'Always-On Display',
          ].map((point) => (
            <div key={point} className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span className="text-white font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
            </div>
          ))}
        </div>
        <p className="text-zinc-400 text-xs leading-relaxed">{item.description}</p>
      </div>

      <div className="edge-glow-top-thin relative" />
      <div className="px-4 py-3">
        <button
          onClick={() => { setExpandedInstructions(expandedInstructions === item.id ? null : item.id); setExpandedFirmware(null); }}
          className="w-full flex items-center justify-between group"
        >
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
            </div>
            <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
            <div className="text-left">
              <span className="text-xs font-semibold text-white transition-colors" style={{display:'block', lineHeight:1, marginBottom:'3px'}}>Setup Instructions</span>
              <p className="text-zinc-400" style={{fontSize:'0.6rem', lineHeight:1, margin:0}}>Follow these steps to connect your device and get it ready.</p>
            </div>
          </div>
          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)', transform: expandedInstructions === item.id ? 'rotate(90deg)' : 'rotate(0deg)'}}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>
        </button>
      </div>
      <div className={`instructions-body${expandedInstructions === item.id ? ' open' : ''}`}>
        <div className="instructions-inner px-4">
            <div className="h-px" style={{background: "rgba(161,161,170,0.25)"}} />
            {/* Setup: a phone sending a picture to the stand, then four simple steps */}
            <div className="mt-3 rounded-xl overflow-hidden border border-zinc-800/40">
              <svg width="100%" viewBox="0 0 680 190" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="A phone sending a picture to the display stand">
                <style>{`
                  @keyframes setupWave { 0% { opacity: 0; transform: scale(.6); } 40% { opacity: 1; } 100% { opacity: 0; transform: scale(1.25); } }
                  @keyframes setupDots { to { stroke-dashoffset: -24; } }
                  .setup-wave { transform-box: fill-box; transform-origin: center; animation: setupWave 2.4s ease-out infinite; }
                  .setup-wave.w2 { animation-delay: .8s; } .setup-wave.w3 { animation-delay: 1.6s; }
                  .setup-dots { animation: setupDots 0.8s linear infinite; }
                  @media (prefers-reduced-motion: reduce) { .setup-wave, .setup-dots { animation: none; } }
                `}</style>
                <defs>
                  <linearGradient id="setupGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#04aff7"/><stop offset="1" stopColor="#f42ae4"/></linearGradient>
                </defs>
                <rect width="680" height="190" fill="#000000"/>
                {/* Phone with the app open */}
                <rect x="150" y="18" width="92" height="156" rx="16" fill="#18181b" stroke="#3f3f46" strokeWidth="2"/>
                <rect x="158" y="30" width="76" height="132" rx="9" fill="#0a0a0d"/>
                <rect x="183" y="23" width="26" height="4" rx="2" fill="#3f3f46"/>
                <text x="196" y="47" textAnchor="middle" fill="#e4e4e7" fontSize="9" fontFamily="sans-serif" fontWeight="700">My Stand</text>
                <rect x="168" y="56" width="56" height="62" rx="6" fill="url(#setupGrad)" opacity="0.9"/>
                <text x="196" y="92" textAnchor="middle" fill="#ffffff" fontSize="11" fontFamily="sans-serif" fontWeight="800">CHEEKS</text>
                <rect x="168" y="128" width="56" height="20" rx="10" fill="url(#setupGrad)"/>
                <text x="196" y="142" textAnchor="middle" fill="#ffffff" fontSize="9" fontFamily="sans-serif" fontWeight="700">Send</text>
                {/* Signal travelling to the stand */}
                <line x1="262" y1="98" x2="420" y2="98" stroke="url(#setupGrad)" strokeWidth="3" strokeLinecap="round" strokeDasharray="2 10" className="setup-dots"/>
                <circle cx="341" cy="98" r="18" fill="none" stroke="#04aff7" strokeWidth="2" className="setup-wave"/>
                <circle cx="341" cy="98" r="18" fill="none" stroke="#8b5cf6" strokeWidth="2" className="setup-wave w2"/>
                <circle cx="341" cy="98" r="18" fill="none" stroke="#f42ae4" strokeWidth="2" className="setup-wave w3"/>
                {/* The display stand: its first product photo, with a soft glow behind it */}
                <ellipse cx="510" cy="150" rx="78" ry="12" fill="#f42ae4" opacity="0.18"/>
                <image href="/digital_stand.png" x="420" y="8" width="180" height="176" preserveAspectRatio="xMidYMid meet"/>
              </svg>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {item.steps.map((step, i) => (
                <div key={i} className="flex flex-col gap-2 rounded-xl bg-black/30 border border-zinc-800/40 p-3">
                  <div className="flex items-center justify-between">
                    <span className="rounded-full px-2.5 py-1 font-extrabold text-white" style={{fontSize:'0.72rem', letterSpacing:'0.06em', lineHeight:1.1, background:'linear-gradient(135deg,#04aff7,#f42ae4)', boxShadow:'0 2px 12px rgba(244,42,228,0.4)'}}>STEP {i + 1}</span>
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{background:'rgba(4,175,247,0.1)', border:'1px solid rgba(4,175,247,0.3)'}}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#04aff7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    {({
                      download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
                      power: <><path d="M18.36 6.64a9 9 0 1 1-12.73 0"/><line x1="12" y1="2" x2="12" y2="12"/></>,
                      wifi: <><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></>,
                      send: <><path d="M22 2 11 13"/><path d="M22 2 15 22 11 13 2 9 22 2z"/></>,
                    })[step.icon]}
                      </svg>
                    </span>
                  </div>
                  <p className="text-white text-xs font-semibold">{step.title}</p>
                  <p className="text-zinc-400 text-[11px] leading-relaxed">{step.detail}</p>
                  {step.icon === 'download' && (
                    <div className="flex items-center gap-1.5 mt-auto">
                      <span className="flex items-center gap-1 rounded-md px-2 py-1 bg-black/60 border border-zinc-700/60 text-white" style={{fontSize:'0.6rem'}}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="#ffffff"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>
                        iPhone
                      </span>
                      <span className="flex items-center gap-1 rounded-md px-2 py-1 bg-black/60 border border-zinc-700/60 text-white" style={{fontSize:'0.6rem'}}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="#3DDC84"><path d="M18.4395 5.5586c-.675 1.1664-1.352 2.3318-2.0274 3.498-.0366-.0155-.0742-.0286-.1113-.043-1.8249-.6957-3.484-.8-4.42-.787-1.8551.0185-3.3544.4643-4.2597.8203-.084-.1494-1.7526-3.021-2.0215-3.4864a1.1451 1.1451 0 0 0-.1406-.1914c-.3312-.364-.9054-.4859-1.379-.203-.475.282-.7136.9361-.3886 1.5019 1.9466 3.3696-.0966-.2158 1.9473 3.3593.0172.031-.4946.2642-1.3926 1.0177C2.8987 12.176.452 14.772 0 18.9902h24c-.119-1.1108-.3686-2.099-.7148-3.0703-.7287-2.0024-1.8228-3.6011-3.1504-4.8828a12.2948 12.2948 0 0 0-2.1953-1.6934c.6233-1.0792 1.2487-2.1574 1.8711-3.2363.1863-.3256.2167-.7024.1074-1.0293a1.1352 1.1352 0 0 0-.6016-.6894c-.4914-.2328-1.1092-.0812-1.3769.3291zm-.0391 8.461c.3636.5655.2068 1.3243-.3516 1.6954-.5586.3712-1.3123.2132-1.6758-.3524-.3636-.5655-.2068-1.3244.3516-1.6955.5587-.371 1.3123-.2131 1.6758.3525zM7.164 13.9786c.5584.3711.7151 1.13.3516 1.6955-.3635.5655-1.1172.7235-1.6758.3524-.5587-.3711-.7154-1.13-.3517-1.6955.3636-.5656 1.1173-.7235 1.6759-.3524z"/></svg>
                        Android
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* One-time note */}
            <div className="mt-3 flex items-center gap-2 px-1">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <p className="text-zinc-400 text-[11px] leading-relaxed">You only need to do this once. Next time, just plug it in and it reconnects by itself.</p>
            </div>

            {/* Factory reset note */}
            <div className="mt-2 flex items-center gap-2 px-1">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#04aff7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              <p className="text-zinc-400 text-[11px] leading-relaxed">Moving to a new Wi-Fi? Hold the reset button while plugging it in for 5 seconds, then add it in the app again.</p>
            </div>

            {/* Troubleshooting */}
            <div className="mt-2 px-1 pb-3">
              <a href="https://cheeks.uk/docs" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-150 active:scale-95" style={{background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)'}}>
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'rgba(239,68,68,0.15)'}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                </div>
                <p className="text-zinc-400 text-[11px] leading-relaxed">Need help using this device? Support is available via <span className="text-cyan-400">Discord</span>.</p>
              </a>
            </div>
          </div>
        </div>

      {/* Update Firmware bar */}
      <div className="edge-glow-top-thin relative" />
      <div className="px-4 py-3 flex items-center justify-between cursor-pointer" onClick={() => { setExpandedFirmware(expandedFirmware === item.id ? null : item.id); setExpandedInstructions(null); }}>
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)'}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
              </svg>
            </div>
            <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
            <div>
              <div style={{display:'flex', alignItems:'center', gap:'6px', marginBottom:'3px'}}>
                <span className="text-xs font-semibold text-white" style={{display:'block', lineHeight:1}}>Update Firmware</span>
                <span className="font-mono" style={{fontSize:'0.55rem', color:'#4ade80', lineHeight:1}}>Latest: {LATEST_VERSION}</span>
              </div>
              <p className="text-zinc-400" style={{fontSize:'0.6rem', lineHeight:1, margin:0}}>Keep your device up to date with the latest features.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {deviceVersion && <span className="font-mono" style={{fontSize:'0.55rem', color:'#ffffff'}}>{deviceName}: {deviceVersion}</span>}
            <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-transform duration-300" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)', transform: expandedFirmware === item.id ? 'rotate(90deg)' : 'rotate(0deg)'}}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </div>
          </div>
        </div>

        <div className={`instructions-body${expandedFirmware === item.id ? ' open' : ''}`}>
          <div className="instructions-inner">
            <div className="h-px mx-4" style={{background: "rgba(161,161,170,0.25)"}} />
            <div className="px-4 pt-4 pb-4">
            <div className="rounded-xl border border-zinc-800/40 overflow-hidden">

              {/* State: unsupported */}
              {firmwareState === 'unsupported' && (
                <div className="px-4 py-4 flex flex-col gap-2">
                  <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(251,191,36,0.08)', border:'1px solid rgba(251,191,36,0.25)'}}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    <div>
                      <p className="text-amber-400 text-xs font-semibold">Browser not supported</p>
                      <p className="text-zinc-400 mt-0.5" style={{fontSize:'0.6rem'}}>Please use Chrome or Edge to use this feature.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* State: disconnected */}
              {firmwareState === 'disconnected' && (
                <div className="px-4 py-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between bg-black/60 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-zinc-700 flex-shrink-0"/>
                      <span className="text-white text-xs font-mono">Not connected</span>
                    </div>
                    <span className="text-zinc-400 font-mono" style={{fontSize:'0.6rem'}}>Current: v1.2.4</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400" style={{fontSize:'0.65rem'}}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Plug your stand into your computer via USB, then click connect.
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); connectSerial(); }}
                    className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-all active:scale-95 flex items-center justify-center gap-2"
                    style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    Connect Device via USB
                  </button>
                </div>
              )}

              {/* State: connecting */}
              {firmwareState === 'connecting' && (
                <div className="px-4 py-4 flex flex-col items-center gap-3">
                  <svg className="animate-spin" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#04aff7" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10" strokeOpacity="0.15"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
                  <div className="text-center">
                    <p className="text-white text-xs font-semibold">Connecting…</p>
                    <p className="text-zinc-400 mt-0.5" style={{fontSize:'0.6rem'}}>Reading device info via serial</p>
                  </div>
                </div>
              )}

              {/* State: connected */}
              {firmwareState === 'connected' && (
                <div className="px-4 py-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between bg-black/60 rounded-lg px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-400" style={{boxShadow:'0 0 6px rgba(74,222,128,0.6)'}}/>
                      <span className="text-white text-xs font-semibold">{deviceName || 'Digital Display Stand'}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-zinc-400 font-mono" style={{fontSize:'0.6rem'}}>Digital Display Stand</p>
                      <p className="text-cyan-400 font-mono font-semibold" style={{fontSize:'0.6rem'}}>{deviceVersion || 'v1.0.0'}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFirmwareState('checking');
                      setTimeout(() => {
                        const isLatest = deviceVersion === LATEST_VERSION;
                        setFirmwareState(isLatest ? 'uptodate' : 'available');
                      }, 2000);
                    }}
                    className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-all active:scale-95"
                    style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}
                  >Check for Updates</button>
                  <button onClick={(e) => { e.stopPropagation(); disconnectSerial(); }} className="text-white text-[10px] text-center w-full hover:text-white transition-colors">Disconnect</button>
                </div>
              )}

              {/* State: checking */}
              {firmwareState === 'checking' && (
                <div className="px-4 py-4 flex flex-col items-center gap-3">
                  <svg className="animate-spin" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#04aff7" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10" strokeOpacity="0.15"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>
                  <div className="text-center">
                    <p className="text-white text-xs font-semibold">Checking for updates…</p>
                    <p className="text-zinc-400 mt-0.5" style={{fontSize:'0.6rem'}}>cheeks.uk/firmware/esp32c6</p>
                  </div>
                </div>
              )}

              {/* State: up to date */}
              {firmwareState === 'uptodate' && (
                <div className="px-4 py-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.2)'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <div>
                      <p className="text-green-400 text-xs font-semibold">Your firmware is up to date</p>
                      <p className="text-zinc-400 mt-0.5" style={{fontSize:'0.6rem'}}>{LATEST_VERSION} — latest release</p>
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setFirmwareState('connected'); }} className="text-white text-[10px] text-center w-full hover:text-white transition-colors">Back</button>
                </div>
              )}

              {/* State: update available */}
              {firmwareState === 'available' && (
                <div className="px-4 py-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(4,175,247,0.06)', border:'1px solid rgba(4,175,247,0.2)'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#04aff7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="8 17 12 21 16 17"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.88 18.09A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.96"/></svg>
                    <div className="flex-1">
                      <p className="text-cyan-400 text-xs font-semibold">Update available — {LATEST_VERSION}</p>
                      <p className="text-zinc-400 mt-0.5" style={{fontSize:'0.6rem'}}>Released 3 Apr 2026 · 284 KB</p>
                    </div>
                  </div>
                  <div className="text-white bg-black/40 rounded-lg px-3 py-2" style={{fontSize:'0.6rem'}}>
                    <p className="text-white font-semibold mb-1">What's new in {LATEST_VERSION}</p>
                    <p>• Faster Wi-Fi reconnection on boot</p>
                    <p>• Stream Deck stability improvements</p>
                    <p>• Fixed rare display flicker on update</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); runOTA(); }}
                    className="w-full py-2 rounded-lg text-xs font-semibold text-white transition-all active:scale-95"
                    style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}
                  >Install Update</button>
                  <button onClick={(e) => { e.stopPropagation(); setFirmwareState('connected'); }} className="text-white text-[10px] text-center w-full hover:text-white transition-colors">Cancel</button>
                </div>
              )}

              {/* State: updating */}
              {firmwareState === 'updating' && (
                <div className="px-4 py-4 flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-xs font-semibold">Installing {LATEST_VERSION}…</span>
                    <span className="text-cyan-400 font-mono text-xs">{Math.round(firmwareProgress)}%</span>
                  </div>
                  <div className="w-full h-2 rounded-full bg-zinc-800/60 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-200" style={{width:`${firmwareProgress}%`, background:'linear-gradient(90deg,#04aff7,#f42ae4)'}}/>
                  </div>
                  <p className="text-zinc-400 text-[10px]">Do not unplug your device during this process.</p>
                </div>
              )}

              {/* State: done */}
              {firmwareState === 'done' && (
                <div className="px-4 py-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(74,222,128,0.08)', border:'1px solid rgba(74,222,128,0.2)'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <div>
                      <p className="text-green-400 text-xs font-semibold">Successfully updated to {LATEST_VERSION}</p>
                      <p className="text-zinc-400 mt-0.5" style={{fontSize:'0.6rem'}}>Device is restarting — this may take a few seconds.</p>
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); disconnectSerial(); }} className="text-white text-[10px] text-center w-full hover:text-white transition-colors">Done</button>
                </div>
              )}

              {/* State: error */}
              {firmwareState === 'error' && (
                <div className="px-4 py-4 flex flex-col gap-3">
                  <div className="flex items-center gap-2 p-3 rounded-lg" style={{background:'rgba(251,113,133,0.08)', border:'1px solid rgba(251,113,133,0.2)'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fb7185" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <div>
                      <p className="text-rose-400 text-xs font-semibold">Update failed</p>
                      <p className="text-zinc-400 mt-0.5" style={{fontSize:'0.6rem'}}>Check your device is connected to Wi-Fi and try again.</p>
                    </div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setFirmwareState('connected'); }} className="text-white text-[10px] text-center w-full hover:text-white transition-colors">Try again</button>
                </div>
              )}

            </div>
            {/* Discord firmware notification note */}
            <div className="mt-3 px-1 pb-1">
              <a href="https://cheeks.uk/docs" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2 rounded-xl transition-all duration-150 active:scale-95" style={{background:'rgba(234,179,8,0.08)', border:'1px solid rgba(234,179,8,0.2)'}}>
                <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'rgba(234,179,8,0.15)'}}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{animation:'bellRing 2s ease-in-out infinite', transformOrigin:'top center'}}>
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                  </svg>
                </div>
                <p className="text-zinc-400 text-[11px] leading-relaxed">Be notified on <span className="text-cyan-400">Discord</span> of the latest firmware updates.</p>
              </a>
            </div>
          </div>
        </div>
        </div>

    </div>
  );

  return (
    <div
      className="bg-black flex flex-col relative"
      style={{ minHeight: '100svh' }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <style>{"@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700;800&family=Outfit:wght@400;500;600;700;800&family=Barlow+Condensed:wght@500;600;700&display=swap');"}</style>

      {/* ── AMBIENT NEON GLOW (fixed, behind everything) — hidden on Builder/Battle/Game ── */}
      {activeTab !== 'builder' && activeTab !== 'battle' && activeTab !== 'toge-dash' && (
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 900px 700px at 8% -5%, rgba(4,175,247,0.10), transparent 60%), radial-gradient(ellipse 900px 700px at 95% 15%, rgba(244,42,228,0.08), transparent 60%), radial-gradient(ellipse 900px 700px at 0% 85%, rgba(244,42,228,0.07), transparent 60%), radial-gradient(ellipse 900px 700px at 100% 105%, rgba(4,175,247,0.09), transparent 60%)',
          }}
        />
      )}
      {/* ── SUBTLE DIAGONAL STRIPES (fixed, behind everything) — hidden on Builder/Battle/Game, starts below the top bar ── */}
      {activeTab !== 'builder' && activeTab !== 'battle' && activeTab !== 'toge-dash' && (
        <div
          className="absolute left-0 right-0 bottom-0 pointer-events-none"
          style={{
            top: maintenanceMode ? '0px' : '100px',
            backgroundImage: 'repeating-linear-gradient(135deg, transparent 0px, transparent 140px, rgba(4,175,247,0.16) 140px, rgba(4,175,247,0.16) 146px, transparent 146px, transparent 280px, rgba(244,42,228,0.16) 280px, rgba(244,42,228,0.16) 286px)',
          }}
        />
      )}

      {/* ── BANNER ── */}
      {bannerText && !maintenanceMode && (
        <div className="relative z-50 flex items-center justify-center gap-2 py-2 px-4 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-lg border-b border-zinc-700/70">
          <span className="text-white text-[11px] font-medium tracking-wide leading-none">
            {(() => {
              const linkRegex = /\[(.*?)\]\((.*?)\)/;
              const match = bannerText.match(linkRegex);
              if (match) {
                const [fullMatch, label, url] = match;
                const [before, after] = bannerText.split(fullMatch);
                return (
                  <>
                    {before}
                    <a href={url} onClick={url.startsWith('http://cheeks.uk/#') || url.startsWith('/#') ? (e) => { e.preventDefault(); const tab = url.split('#')[1]; if (['profile','inventory','creations'].includes(tab)) { switchTab(tab); } } : undefined} className="ml-1.5 bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-full text-[10px] transition-all corner-glow stroke-thin inline-flex items-center hover:scale-105 active:scale-95 font-semibold">
                      {label}
                    </a>
                    {after}
                  </>
                );
              }
              return bannerText;
            })()}
          </span>
        </div>
      )}

      {/* ── TOP NAV ── */}
      <div className={maintenanceMode ? 'relative z-[100]' : 'bg-black/80 backdrop-blur-lg edge-glow-bottom relative z-[100]'}>
        <div className="max-w-7xl mx-auto px-4 pt-4 pb-7">
          <div className="flex items-center justify-between">

            {/* Left: empty spacer — balances the login button so the logo stays centered */}
            <div className="flex-1"></div>

            {/* Centre: logo — hidden during maintenance, shown bigger in the page body instead */}
            {!maintenanceMode && (
            <div className="flex flex-col justify-center items-center gap-1">
              <div
                className="flex items-center gap-1.5"
                ref={(el) => {
                  if (!el) return;
                  const run = () => {
                    const xEl = el.querySelector('.x-pulse');
                    const imgs = el.querySelectorAll('img');
                    const sparks = el.querySelectorAll('.spark');
                    if (xEl) xEl.classList.add('pulse-active');
                    imgs.forEach((img) => img.classList.add('logo-bounce-active'));
                    sparks.forEach((s) => s.classList.add('spark-active'));
                    setTimeout(() => {
                      if (xEl) xEl.classList.remove('pulse-active');
                      imgs.forEach((img) => img.classList.remove('logo-bounce-active'));
                      sparks.forEach((s) => s.classList.remove('spark-active'));
                    }, 2200);
                  };
                  if (!el._pulseInit) {
                    el._pulseInit = true;
                    setTimeout(run, 1500);
                    setInterval(run, 5000);
                  }
                }}
              >
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  {[
                    { top: '-5%',  left: '10%',  tx: '-10px', ty: '-22px', delay: '0s',    size: '3px', color: '#04aff7' },
                    { top: '5%',   left: '85%',  tx: '24px',  ty: '-14px', delay: '0.08s', size: '5px', color: '#f42ae4' },
                    { top: '40%',  left: '-8%',  tx: '-26px', ty: '4px',   delay: '0.15s', size: '4px', color: '#f42ae4' },
                    { top: '60%',  left: '102%', tx: '22px',  ty: '10px',  delay: '0.03s', size: '3px', color: '#04aff7' },
                    { top: '95%',  left: '20%',  tx: '-14px', ty: '20px',  delay: '0.2s',  size: '5px', color: '#04aff7' },
                    { top: '90%',  left: '70%',  tx: '16px',  ty: '22px',  delay: '0.1s',  size: '3px', color: '#f42ae4' },
                    { top: '15%',  left: '50%',  tx: '2px',   ty: '-24px', delay: '0.18s', size: '4px', color: '#f42ae4' },
                    { top: '75%',  left: '45%',  tx: '-4px',  ty: '24px',  delay: '0.06s', size: '3px', color: '#04aff7' },
                  ].map((s, i) => (
                    <span key={i} className="spark" style={{ top: s.top, left: s.left, width: s.size, height: s.size, '--tx': s.tx, '--ty': s.ty, background: s.color, animationDelay: s.delay }} />
                  ))}
                  <img src="/cheeks_logo.svg" alt="Cheeks Logo" className="h-10 object-contain filter drop-shadow-md opacity-90 logo-bounce" style={{ position: 'relative', zIndex: 2 }} />
                </span>
                <span
                  className="x-pulse font-semibold"
                  style={{ fontSize: '1.1rem' }}
                >×</span>
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  {[
                    { top: '-5%',  left: '12%',  tx: '-12px', ty: '-20px', delay: '0.12s', size: '4px', color: '#f42ae4' },
                    { top: '8%',   left: '88%',  tx: '22px',  ty: '-16px', delay: '0.2s',  size: '3px', color: '#04aff7' },
                    { top: '45%',  left: '-8%',  tx: '-24px', ty: '2px',   delay: '0.27s', size: '5px', color: '#04aff7' },
                    { top: '55%',  left: '103%', tx: '24px',  ty: '8px',   delay: '0.15s', size: '3px', color: '#f42ae4' },
                    { top: '92%',  left: '22%',  tx: '-16px', ty: '22px',  delay: '0.32s', size: '4px', color: '#f42ae4' },
                    { top: '88%',  left: '72%',  tx: '14px',  ty: '20px',  delay: '0.22s', size: '5px', color: '#04aff7' },
                    { top: '18%',  left: '52%',  tx: '4px',   ty: '-22px', delay: '0.3s',  size: '3px', color: '#04aff7' },
                    { top: '78%',  left: '48%',  tx: '-2px',  ty: '22px',  delay: '0.18s', size: '4px', color: '#f42ae4' },
                  ].map((s, i) => (
                    <span key={i} className="spark" style={{ top: s.top, left: s.left, width: s.size, height: s.size, '--tx': s.tx, '--ty': s.ty, background: s.color, animationDelay: s.delay }} />
                  ))}
                  <img src="/cheeky_white.svg" alt="Cheeky Cards" className="h-10 object-contain filter drop-shadow-md opacity-90 logo-bounce" style={{ position: 'relative', zIndex: 2, animationDelay: '0.12s' }} />
                </span>
              </div>
              <style>{`
                @keyframes subtitlePulse {
                  0%   { color: #52525b; }
                  30%  { color: #d4d4d8; }
                  70%  { color: #d4d4d8; }
                  100% { color: #52525b; }
                }
                .header-subtitle { color: #52525b; }
                .header-subtitle.shimmer-active {
                  animation: subtitlePulse 2.2s ease-in-out forwards;
                }
                .x-pulse { color: #52525b; }
                .x-pulse.pulse-active {
                  animation: subtitlePulse 2.2s ease-in-out forwards;
                }
                @keyframes logoBounce {
                  0%   { transform: scale(1) translateY(0); }
                  30%  { transform: scale(1.14) translateY(-4px); }
                  50%  { transform: scale(0.96) translateY(1px); }
                  70%  { transform: scale(1.05) translateY(-1px); }
                  100% { transform: scale(1) translateY(0); }
                }
                .logo-bounce.logo-bounce-active {
                  animation: logoBounce 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) both;
                }
                @keyframes sparkFly {
                  0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.4); }
                  20%  { opacity: 1; }
                  100% { opacity: 0; transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(1); }
                }
                .spark {
                  position: absolute;
                  border-radius: 50%;
                  opacity: 0;
                  pointer-events: none;
                  z-index: 1;
                }
                .spark.spark-active {
                  animation: sparkFly 0.7s ease-out;
                }
              `}</style>
              <p
                className="header-subtitle font-semibold uppercase text-center"
                style={{fontSize:'0.45rem', letterSpacing:'0.18em', lineHeight:1.5, fontFamily:"'Barlow Condensed', 'Arial Narrow', sans-serif", whiteSpace:'nowrap'}}
                ref={(el) => {
                  if (!el) return;
                  let active = false;
                  const run = () => {
                    el.classList.add('shimmer-active');
                    setTimeout(() => el.classList.remove('shimmer-active'), 2200);
                  };
                  if (!el._shimmerInit) {
                    el._shimmerInit = true;
                    setTimeout(run, 1500);
                    setInterval(run, 5000);
                  }
                }}
              >Trading Cards, Collectibles, Custom Accessories &amp; More</p>

            </div>
            )}

            {/* Right: login/account — only rendered here during maintenance mode, since the normal menu (which holds this button) is hidden then */}
            <div className="flex items-center justify-end flex-1">
              {maintenanceMode && (
                <div className="relative" data-user-menu>
                  {!discordUser ? (
                    <button
                      onClick={handleDiscordLogin}
                      className="flex flex-col items-center justify-center text-white transition-all duration-200 active:scale-95 flex-shrink-0 bg-zinc-800/50 hover:bg-zinc-700/50 border border-zinc-700"
                      style={{width:'36px', height:'36px', borderRadius:'50%'}}
                      title="Login"
                    >
                      <LogIn className="h-3 w-3 flex-shrink-0" />
                      <span className="text-white font-bold leading-none" style={{fontSize:'0.38rem', letterSpacing:'0.08em'}}>LOGIN</span>
                    </button>
                  ) : (
                    <div className="relative" data-user-menu>
                      <button
                        onClick={() => setShowUserMenu(prev => !prev)}
                        className="flex flex-col items-center justify-center text-white transition-all duration-200 active:scale-95 flex-shrink-0 bg-zinc-800/50 hover:bg-zinc-700/50 border border-zinc-700"
                        style={{width:'44px', minHeight:'36px', borderRadius:'10px', padding:'4px 2px'}}
                      >
                        <img
                          src={`https://cdn.discordapp.com/avatars/${discordUser.id}/${discordUser.avatar}.png`}
                          alt="User"
                          className="h-4 w-4 rounded-full flex-shrink-0"
                        />
                        <span className="font-bold leading-none truncate" style={{fontSize:'0.38rem', letterSpacing:'0.02em', maxWidth:'40px'}}>{discordUser.username}</span>
                      </button>
                      {showUserMenu && (
                        <div className="absolute right-0 top-full mt-1 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl z-[999] min-w-[220px]">
                          <div className="px-3 py-2 border-b border-zinc-800">
                            <p className="text-white text-xs font-semibold truncate">{discordUser.username}</p>
                            <p className="text-zinc-400 text-[10px]">Discord</p>
                          </div>

                          {isAdminDiscordUser && (
                            <div className="px-3 py-2 border-b border-zinc-800">
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <span className="text-zinc-300 text-[11px] font-semibold">Maintenance Mode</span>
                                <span
                                  className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                                  style={{
                                    background: maintenanceMode ? 'rgba(239,68,68,0.15)' : 'rgba(35,165,90,0.15)',
                                    color: maintenanceMode ? '#ef4444' : '#23a55a',
                                  }}
                                >
                                  {maintenanceMode ? 'ON' : 'OFF'}
                                </span>
                              </div>
                              <button
                                onClick={handleToggleMaintenanceMode}
                                disabled={maintenanceBusy}
                                className="w-full px-2 py-1.5 rounded-md text-white text-[11px] font-semibold disabled:opacity-40 transition-all"
                                style={{ background: maintenanceMode ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#ef4444,#dc2626)' }}
                              >
                                {maintenanceBusy ? 'Updating…' : maintenanceMode ? 'Turn Site Back On' : 'Turn On Maintenance Mode'}
                              </button>
                              {maintenanceError && (
                                <p className="text-red-400 text-[10px] mt-1">{maintenanceError}</p>
                              )}
                            </div>
                          )}

                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-3 py-2 text-red-400 hover:bg-zinc-800/50 text-xs font-medium transition-colors rounded-b-lg flex items-center space-x-2"
                          >
                            <LogIn className="h-3 w-3 rotate-180" />
                            <span>Log out</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ── MAINTENANCE MODE ── */}
      {maintenanceMode && (
        <main className="flex-1 flex flex-col items-center justify-center text-center px-6" style={{ minHeight: 'calc(100vh - 80px)' }}>
          <div className="flex items-center justify-center flex-wrap" style={{ gap: 'clamp(0.5rem, 3vw, 1.25rem)', maxWidth: '100%' }}>
            <img src="/cheeks_logo.svg" alt="Cheeks Logo" className="object-contain filter drop-shadow-md opacity-90" style={{ height: 'clamp(3rem, 16vw, 7rem)', maxWidth: '38vw' }} />
            <span className="text-zinc-400 font-semibold" style={{ fontSize: 'clamp(1.1rem, 6vw, 2.25rem)' }}>×</span>
            <img src="/cheeky_white.svg" alt="Cheeky Cards" className="object-contain filter drop-shadow-md opacity-90" style={{ height: 'clamp(3rem, 16vw, 7rem)', maxWidth: '38vw' }} />
          </div>
          <p
            className="font-semibold uppercase text-center mt-4"
            style={{ fontSize: 'clamp(0.7rem, 2vw, 0.9rem)', letterSpacing: '0.22em', color: '#52525b', fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif" }}
          >Trading Cards, Collectibles, Custom Accessories &amp; More</p>
        </main>
      )}

      {!maintenanceMode && (
      <>
      {/* Small tab menu — sits below the header bar */}
      <div className="flex items-center justify-center flex-wrap gap-1 mx-auto -mt-4 mb-2 px-2 py-1.5 max-w-fit rounded-full corner-glow bg-gradient-to-br from-zinc-800/85 to-black/90 relative z-[101]">
        {TAB_LIST.map(({ id, label, path, emoji }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => switchTab(id)}
              className={`flex items-center gap-1 rounded-full font-semibold ${isActive ? 'corner-glow stroke-thin' : ''}`}
              style={{
                padding: isActive ? '4px 7px' : '4px 6px',
                fontSize: '0.6rem',
                ...(isActive ? {
                  background: 'black',
                  color: 'white',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
                } : {
                  color: 'white',
                  background: 'transparent',
                  boxShadow: 'none',
                })
              }}
            >
              {emoji ? (
                <span style={isActive ? { fontSize: '11px', lineHeight: 1, filter: 'drop-shadow(0 0 3px rgba(0,0,0,0.8))' } : { fontSize: '11px', lineHeight: 1 }}>{emoji}</span>
              ) : (
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  {path}
                </svg>
              )}
              <span>{label}</span>
            </button>
          );
        })}

        {/* Login / user button */}
        <div className="relative" data-user-menu>
          {!discordUser ? (
            <div className="relative">
              <button
                onClick={handleDiscordLogin}
                className="flex items-center gap-1 rounded-full font-semibold transition-all duration-200"
                style={{ padding: '4px 6px', fontSize: '0.6rem', color: 'white', background: 'transparent' }}
              >
                <span style={{ fontSize: '11px', lineHeight: 1 }}>🔑</span>
                <span>Login</span>
              </button>
              {loginError && (
                <div
                  className="absolute top-full right-0 mt-1.5 w-48 rounded-lg px-2.5 py-2 shadow-xl z-[999] transition-opacity duration-500"
                  style={{backgroundColor:'rgba(15,15,20,0.92)', border:'1px solid rgba(255,102,102,0.4)', opacity: loginErrorVisible ? 1 : 0}}
                >
                  <p className="text-[10px] leading-snug" style={{color:'#FF6666'}}>You must be a member of CHΞΞKSCORD to login.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="relative" data-user-menu>
              <button
                onClick={() => setShowUserMenu(prev => !prev)}
                className="flex items-center gap-1 rounded-full font-semibold transition-all duration-200"
                style={{ padding: '4px 6px', fontSize: '0.6rem', color: 'white', background: 'transparent' }}
              >
                <img
                  src={`https://cdn.discordapp.com/avatars/${discordUser.id}/${discordUser.avatar}.png`}
                  alt="User"
                  className="h-[14px] w-[14px] rounded-full flex-shrink-0"
                />
                <span className="truncate" style={{maxWidth:'70px'}}>{discordUser.username}</span>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-1 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl z-[999] min-w-[220px]">
                  <div className="px-3 py-2 border-b border-zinc-800">
                    <p className="text-white text-xs font-semibold truncate">{discordUser.username}</p>
                    <p className="text-zinc-400 text-[10px]">Discord</p>
                  </div>

                  {isAdminDiscordUser && (
                    <div className="px-3 py-2 border-b border-zinc-800">
                      <div className="flex items-center justify-between gap-2 mb-1.5">
                        <span className="text-zinc-300 text-[11px] font-semibold">Maintenance Mode</span>
                        <span
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                          style={{
                            background: maintenanceMode ? 'rgba(239,68,68,0.15)' : 'rgba(35,165,90,0.15)',
                            color: maintenanceMode ? '#ef4444' : '#23a55a',
                          }}
                        >
                          {maintenanceMode ? 'ON' : 'OFF'}
                        </span>
                      </div>
                      <button
                        onClick={handleToggleMaintenanceMode}
                        disabled={maintenanceBusy}
                        className="w-full px-2 py-1.5 rounded-md text-white text-[11px] font-semibold disabled:opacity-40 transition-all"
                        style={{ background: maintenanceMode ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#ef4444,#dc2626)' }}
                      >
                        {maintenanceBusy ? 'Updating…' : maintenanceMode ? 'Turn Site Back On' : 'Turn On Maintenance Mode'}
                      </button>
                      {maintenanceError && (
                        <p className="text-red-400 text-[10px] mt-1">{maintenanceError}</p>
                      )}
                    </div>
                  )}

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-3 py-2 text-red-400 hover:bg-zinc-800/50 text-xs font-medium transition-colors rounded-b-lg flex items-center space-x-2"
                  >
                    <LogIn className="h-3 w-3 rotate-180" />
                    <span>Log out</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search bar — only shown in inventory */}
      {activeTab === 'inventory' && (
        <div className="bg-black border-b border-zinc-700/70">
            <div className="max-w-7xl mx-auto px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder={`Search… ${currentText}|`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 pr-3 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-sm transition-all duration-200"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <button
                  onClick={handleSearch}
                  className="bg-gradient-to-r from-cyan-500 to-pink-600 text-white p-2.5 rounded-xl transition-all duration-200 active:scale-95 flex-shrink-0"
                >
                  <Search className="h-4 w-4" />
                </button>
              </div>

              {/* Category pills */}
              <div className="flex gap-2 mt-2.5 overflow-x-auto pb-0.5 scrollbar-hide">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3.5 py-1.5 rounded-xl font-medium transition-all duration-200 text-xs whitespace-nowrap flex-shrink-0 ${
                      selectedCategory === cat.id
                        ? 'bg-gradient-to-r from-cyan-500 to-pink-600 text-white shadow-lg'
                        : 'bg-zinc-800/50 text-zinc-400 hover:text-white border border-zinc-700/70'
                    }`}
                  >
                    {cat.name}
                    {cat.id !== 'all' && (
                      <span className="ml-1 opacity-70">({images.filter(img => img.category === cat.id).length})</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

      {/* ══════════════════════════════════════════
          PROFILE TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'profile' && (
        <main className="flex-1">
          <style>{`
            @keyframes profileFadeUp {
              from { opacity: 0; transform: translateY(18px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .profile-panel {
              animation: profileFadeUp 0.45s ease both;
            }
          `}</style>
          <div className="profile-panel max-w-2xl mx-auto px-4 pt-2 pb-6 flex flex-col gap-4">

            {/* Digital Display Stand banner: scrolls down to Product info & firmware updates */}
            <button
              onClick={() => { document.getElementById('product-info')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              className="relative block w-full rounded-2xl overflow-hidden border border-zinc-700/70 bg-black/80 corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] active:scale-[0.98] transition-all duration-150"
              style={{ height: '160px' }}
              aria-label="Digital Display Stand: see product info and firmware updates"
            >
              <img src="/digi_stand_banner.jpg" alt="Digital Display Stand" className="absolute inset-0 w-full h-full object-cover glow-neutral" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              {/* Works with Apple and Android */}
              <span className="absolute right-2.5 top-2.5 flex items-center gap-1.5 pointer-events-none" style={{ zIndex: 30 }} aria-hidden="true">
                <span className="flex items-center justify-center rounded-full" style={{ width: '28px', height: '28px', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(4px)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#ffffff"><path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/></svg>
                </span>
                <span className="flex items-center justify-center rounded-full" style={{ width: '28px', height: '28px', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.18)', backdropFilter: 'blur(4px)' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="#3DDC84"><path d="M18.4395 5.5586c-.675 1.1664-1.352 2.3318-2.0274 3.498-.0366-.0155-.0742-.0286-.1113-.043-1.8249-.6957-3.484-.8-4.42-.787-1.8551.0185-3.3544.4643-4.2597.8203-.084-.1494-1.7526-3.021-2.0215-3.4864a1.1451 1.1451 0 0 0-.1406-.1914c-.3312-.364-.9054-.4859-1.379-.203-.475.282-.7136.9361-.3886 1.5019 1.9466 3.3696-.0966-.2158 1.9473 3.3593.0172.031-.4946.2642-1.3926 1.0177C2.8987 12.176.452 14.772 0 18.9902h24c-.119-1.1108-.3686-2.099-.7148-3.0703-.7287-2.0024-1.8228-3.6011-3.1504-4.8828a12.2948 12.2948 0 0 0-2.1953-1.6934c.6233-1.0792 1.2487-2.1574 1.8711-3.2363.1863-.3256.2167-.7024.1074-1.0293a1.1352 1.1352 0 0 0-.6016-.6894c-.4914-.2328-1.1092-.0812-1.3769.3291zm-.0391 8.461c.3636.5655.2068 1.3243-.3516 1.6954-.5586.3712-1.3123.2132-1.6758-.3524-.3636-.5655-.2068-1.3244.3516-1.6955.5587-.371 1.3123-.2131 1.6758.3525zM7.164 13.9786c.5584.3711.7151 1.13.3516 1.6955-.3635.5655-1.1172.7235-1.6758.3524-.5587-.3711-.7154-1.13-.3517-1.6955.3636-.5656 1.1173-.7235 1.6759-.3524z"/></svg>
                </span>
              </span>
            </button>

            {/* Avatar + name card */}
            <div className="bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] overflow-hidden">
              {/* Banner strip */}
              <div
                className="h-44 relative overflow-hidden glow-neutral"
                style={{ background: '#000000' }}
              >
                <style>{`
                  @keyframes bannerShift {
                    0%   { background-position: 0% 50%; }
                    50%  { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                  }
                  .banner-anim {
                    position: absolute;
                    inset: 0;
                    background-image: url('/wn_banner.png');
                    background-size: cover;
                    background-position: center;
                  }
                  .banner-tint {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(160deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.55) 50%, rgba(0,0,0,0.75) 100%);
                  }
                  .banner-tint::after {
                    content: none;
                  }
                  @keyframes bannerBubbleFloat {
                    0%   { transform: translateY(0) translateX(0); opacity: 0; }
                    10%  { opacity: 0.7; }
                    80%  { opacity: 0.7; }
                    100% { transform: translateY(-200px) translateX(calc(8px * var(--bdrift))); opacity: 0; }
                  }
                  .banner-bubble {
                    position: absolute;
                    bottom: -20px;
                    border-radius: 50%;
                    background: radial-gradient(circle at 50% 50%,
                      rgba(255,255,255,0.04) 0%,
                      rgba(255,255,255,0.12) 60%,
                      rgba(255,255,255,0.35) 100%);
                    box-shadow:
                      inset 0 0 6px rgba(255,255,255,0.3),
                      inset 3px 0 6px rgba(180,100,255,0.25),
                      inset -3px 0 6px rgba(100,180,255,0.25),
                      inset 0 -3px 6px rgba(255,120,180,0.2);
                    mask-image: radial-gradient(circle, white 68%, transparent 71%);
                    -webkit-mask-image: radial-gradient(circle, white 68%, transparent 71%);
                    animation: bannerBubbleFloat linear infinite;
                    pointer-events: none;
                  }
                  .banner-bubble::after {
                    content: '';
                    position: absolute;
                    top: 18%;
                    left: 18%;
                    width: 22%;
                    height: 14%;
                    background: rgba(255,255,255,0.5);
                    border-radius: 50%;
                    transform: rotate(-35deg);
                    filter: blur(1px);
                  }
                `}</style>
                <div className="banner-anim" />
                <div className="banner-tint" />
                {/* Bubbles injected via ref */}
                <div ref={bannerBubbleRef} style={{position:'absolute',inset:0,pointerEvents:'none'}} />
                {/* Rotating customer review over the banner */}
                <ReviewsCarousel />
                {/* Location + time */}
                <div style={{position:'absolute', top:'8px', right:'8px', display:'flex', alignItems:'center', pointerEvents:'none'}}>
                  <div className="corner-glow stroke-thin" style={{
                    display:'flex', alignItems:'center', gap:'4px',
                    background:'#000000',
                    backdropFilter:'blur(6px)',
                    paddingLeft:'8px', paddingRight:'8px', paddingTop:'4px', paddingBottom:'4px',
                    borderRadius:'999px',
                  }}>
                    <img src="https://flagcdn.com/gb.svg" alt="UK" style={{width:'12px',height:'12px',borderRadius:'50%',objectFit:'cover',flexShrink:0}} />
                    <span style={{color:'#ffffff', fontSize:'0.55rem', fontWeight:500, letterSpacing:'0.02em', lineHeight:1, display:'block', paddingTop:'1px'}}>
                      London &nbsp;·&nbsp; {currentTime.toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})} &nbsp;{currentTime.toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit',second:'2-digit'})}
                    </span>
                  </div>
                </div>
              </div>

              <div className="px-4 pb-4 relative glow-neutral edge-glow-top-thin edge-glow-bold" style={{ paddingTop: '3px' }}>
                {/* Activity info — top-right, sits flush under the banner */}
                {myActivity && (
                  <div style={{position:'absolute', top:'0', right:'0', display:'flex', flexDirection:'column', alignItems:'flex-end', zIndex:10}}>
                    <div style={{position:'relative', background:'#0c0c0e', borderLeft:'1px solid rgba(63,63,70,0.5)', borderBottom:'1px solid rgba(63,63,70,0.5)', borderBottomLeftRadius:'12px', padding:'8px 8px 7px 8px', display:'flex', alignItems:'center', gap:'6px', lineHeight:'1.2', whiteSpace:'normal', textAlign:'right'}}>
                      <BorderStreak />
                      {/* Album art / game artwork, as tall as the text. order:1 puts it on the right.
                          Cross-fades between artworks and holds its space so the text never jumps */}
                      <AlbumArt src={myActivity.image} size={activityTextHeight} style={{order:1}} />
                      <div ref={el => { if (el && el.offsetHeight !== activityTextHeight) setActivityTextHeight(el.offsetHeight); }} style={{display:'flex', flexDirection:'column', gap:'1px', alignItems:'flex-end'}}>
                      {/* Title row is never cut off — the box widens to fit it, and the lines below use that width */}
                      <div ref={el => { if (el && el.offsetWidth !== activityTitleWidth) setActivityTitleWidth(el.offsetWidth); }} className="text-white font-semibold uppercase tracking-widest" style={{display:'flex', alignItems:'center', gap:'3px', fontSize:'0.625rem', letterSpacing:'0.01em', whiteSpace:'nowrap', lineHeight:1, marginBottom:'1px'}}>
                        {(myActivity.type || '').toLowerCase().includes('watch') && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0, marginRight:'2px'}}><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>}
                        {(myActivity.type || '').toLowerCase().includes('play') && <img src="/controller.svg" width="10" height="10" style={{flexShrink:0, marginRight:'2px', filter:'brightness(0) saturate(100%) invert(52%) sepia(98%) saturate(346%) hue-rotate(95deg) brightness(93%) contrast(92%)'}} />}
                        {(myActivity.type || '').toLowerCase().includes('listen') && (
                          <>
                            <style>{`
                              @keyframes musicWaveBar { 0%, 100% { height: 25%; } 50% { height: 100%; } }
                              .music-wave-bar { width: 2px; border-radius: 1px; background: #23a55a; animation: musicWaveBar 0.9s ease-in-out infinite; }
                            `}</style>
                            <div style={{display:'flex', alignItems:'center', gap:'1.5px', height:'10px', flexShrink:0, marginRight:'2px'}}>
                              <span className="music-wave-bar" style={{animationDelay:'0s'}} />
                              <span className="music-wave-bar" style={{animationDelay:'0.2s'}} />
                              <span className="music-wave-bar" style={{animationDelay:'0.4s'}} />
                              <span className="music-wave-bar" style={{animationDelay:'0.1s'}} />
                            </div>
                          </>
                        )}
                        {(myActivity.type || '').toLowerCase().includes('stream') && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0, marginRight:'2px'}}><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>}
                        {!['watch','play','listen','stream'].some(k => (myActivity.type || '').toLowerCase().includes(k)) && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0, marginRight:'2px'}}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>}
                        <span>{(myActivity.type || '').toLowerCase().includes('listen') && isLive ? 'STREAM MUSIC ID' : myActivity.type}</span>
                      </div>
                      {!(myActivity.name || '').toLowerCase().includes('youtube music') && <ScrollingText className="text-white font-medium" style={{fontSize:'0.6rem'}} maxWidth={`${Math.max(95, activityTitleWidth)}px`} text={myActivity.name} />}
                      {myActivity.detail && !(myActivity.detail || '').toLowerCase().includes('youtube music') && <ScrollingText className="text-white" style={{fontSize:'0.6rem'}} maxWidth={`${Math.max(95, activityTitleWidth)}px`} text={myActivity.detail} />}
                      {myActivity.state && !(myActivity.state || '').toLowerCase().includes('youtube music') && <ScrollingText className="text-white" style={{fontSize:'0.6rem'}} maxWidth={`${Math.max(95, activityTitleWidth)}px`} text={myActivity.state} />}
                      {elapsed && <div style={{display:'flex', alignItems:'center', gap:'2px', color:'#23a55a', fontSize:'0.6rem', fontWeight:500, lineHeight:1, marginTop:'2px'}}>
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#23a55a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>
                        {elapsed}
                      </div>}
                      </div>
                    </div>
                  </div>
                )}
                {/* Avatar row */}
                <div className="flex items-start" style={{ marginTop: -36 }}>

                  {/* Avatar — left, relative so activity text can be absolute */}
                  <div className="relative flex-shrink-0">
                    {/* Floating hearts on load */}
                    <style>{`
                      @keyframes heartFloat {
                        0%   { transform: translateY(0) scale(0.6) rotate(var(--hr)); opacity: 0; }
                        15%  { opacity: 1; transform: translateY(-10px) scale(1) rotate(var(--hr)); }
                        80%  { opacity: 0.8; }
                        100% { transform: translateY(-70px) translateX(var(--hx)) scale(0.7) rotate(var(--hr)); opacity: 0; }
                      }
                      .pfp-heart {
                        position: absolute;
                        pointer-events: none;
                        animation: heartFloat 1.4s ease-out forwards;
                        font-size: 14px;
                        filter: drop-shadow(0 0 3px rgba(251,113,133,0.6));
                        z-index: 23;
                      }
                    `}</style>
                    {showHearts && [
                      { delay: '0s',    left: '30%', hx: '-8px',  hr: '-15deg', size: '13px' },
                      { delay: '0.15s', left: '55%', hx: '6px',   hr: '10deg',  size: '10px' },
                      { delay: '0.3s',  left: '20%', hx: '-4px',  hr: '-8deg',  size: '16px' },
                      { delay: '0.45s', left: '70%', hx: '10px',  hr: '20deg',  size: '11px' },
                      { delay: '0.6s',  left: '45%', hx: '-2px',  hr: '-5deg',  size: '14px' },
                      { delay: '0.1s',  left: '80%', hx: '5px',   hr: '12deg',  size: '9px'  },
                    ].map((h, i) => (
                      <span
                        key={i}
                        className="pfp-heart"
                        style={{
                          bottom: '10px',
                          left: h.left,
                          animationDelay: h.delay,
                          fontSize: h.size,
                          '--hx': h.hx,
                          '--hr': h.hr,
                        }}
                      >💗</span>
                    ))}
                    {/* Thought bubble */}
                    {myStatus && (
                      <div style={{position: 'absolute', top: '-8px', left: '75px', pointerEvents: 'none'}}>
                        <div className="corner-stroke stroke-thin" style={{background: 'rgba(20,20,23,0.85)', borderRadius: '12px', padding: '4px 10px', whiteSpace: 'nowrap', marginLeft: '14px', lineHeight: 1, display: 'inline-flex', alignItems: 'center', boxShadow: 'inset 2px 2px 3px rgba(4,175,247,0.8), inset -2px -2px 3px rgba(244,42,228,0.8)'}}>
                          <span style={{color: '#ffffff', fontSize: '0.6rem', fontWeight: 500, display: 'block'}}>{myStatus}</span>
                        </div>
                        <div className="corner-stroke stroke-thin" style={{width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(20,20,23,0.85)', marginTop: '3px', marginLeft: '8px', boxShadow: 'inset 1px 1px 1.5px rgba(4,175,247,0.8), inset -1px -1px 1.5px rgba(244,42,228,0.8)'}}></div>
                      </div>
                    )}
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-zinc-900 flex items-center justify-center shadow-xl" style={{border: '1px solid rgba(39,39,42,0.5)', position: 'relative', zIndex: 21}}>
                      <img src="/pfp.jpg" alt="Profile" className="w-full h-full object-cover" />
                    </div>
                    {/* Presence status dot */}
                    {(() => {
                      const s = myPresence || 'offline';
                      const dotColor = s === 'online' ? '#23a55a' : s === 'idle' ? '#f0b232' : s === 'dnd' ? '#f23f43' : '#80848e';
                      if (s === 'idle') return (
                        <svg
                          className="absolute bottom-0.5 right-0.5"
                          width="20" height="20" viewBox="0 0 20 20"
                          style={{ zIndex: 22 }}
                          title="Idle"
                        >
                          <circle cx="10" cy="10" r="10" fill="#18181b"/>
                          <circle cx="10" cy="10" r="6.5" fill="#f0b232"/>
                          <circle cx="7.5" cy="7.5" r="5" fill="#18181b"/>
                        </svg>
                      );
                      return (
                        <div
                          className="absolute bottom-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ background: dotColor, border: '3.5px solid #18181b', zIndex: 22 }}
                          title={s === 'dnd' ? 'Do Not Disturb' : s.charAt(0).toUpperCase() + s.slice(1)}
                        >
                          {s === 'dnd' && <div style={{width:'55%',height:'2px',background:'white',borderRadius:2}} />}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Badge row — sits to the right of the avatar, aligned to its bottom */}
                  <div className="flex items-end gap-1.5 ml-3 pb-2" style={{marginTop: 'auto'}}>
                    {badges.map((badge) => (
                      <img
                        key={badge.key}
                        title={badge.label}
                        src={badge.img}
                        alt={badge.label}
                        className="w-7 h-7 object-contain"
                      />
                    ))}
                  </div>

                </div>

                {/* Name + bio */}
                <div className="mt-1">
                  <div className="flex items-center gap-1.5">
                    <h1 className="text-white font-bold text-xl leading-tight">cheeks</h1>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="flex-shrink-0 mt-0.5">
                      <defs>
                        <linearGradient id="checkGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#04aff7"/>
                          <stop offset="100%" stopColor="#f42ae4"/>
                        </linearGradient>
                      </defs>
                      <circle cx="12" cy="12" r="10" fill="url(#checkGrad)"/>
                      <path d="M7.5 12.5l3 3 6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {(() => {
                      const s = myPresence || 'offline';
                      const label = s === 'online' ? 'ONLINE' : s === 'idle' ? 'IDLE' : s === 'dnd' ? 'BUSY' : 'OFFLINE';
                      const color = s === 'online' ? '#23a55a' : s === 'idle' ? '#f0b232' : s === 'dnd' ? '#f23f43' : '#80848e';
                      return (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                          style={{ color, background: `${color}22`, border: `1px solid ${color}55` }}
                        >
                          {label}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-xs font-medium mt-0.5" style={{ background: 'linear-gradient(135deg, #04aff7, #f42ae4)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>@togecheeks</p>
                </div>

              </div>
            </div>

            {/* Social Links */}
            <div className="grid grid-cols-2 gap-2.5">

              {/* Whatnot */}
              {isLive && (
                <style>{`
                  @keyframes liveGlow {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0), 0 6px 24px rgba(0,0,0,0.4); }
                    50%      { box-shadow: 0 0 18px 3px rgba(239,68,68,0.65), 0 6px 24px rgba(0,0,0,0.4); }
                  }
                  .whatnot-live { animation: liveGlow 1.8s ease-in-out infinite; }
                `}</style>
              )}
              <button onClick={() => { window.location.href = 'whatnot://user/togecheeks/shows'; setTimeout(() => { window.location.href = 'https://www.whatnot.com/en-GB/user/togecheeks/shows'; }, 1000); }} className={`social-bar-press relative w-full flex items-center gap-2 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-3 py-2.5 active:scale-[0.98] transition-all duration-200 ${isLive ? 'whatnot-live glow-red' : ''}`}>
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <div className="relative">
                    <img src="/wnlogo.png" alt="Whatnot" className="w-7 h-7 object-contain" />
                    {isLive && (
                      <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" style={{border:'1.5px solid #1e293b'}}></span></span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-start gap-1">
                  <div className="text-white font-semibold leading-none text-left uppercase tracking-widest text-[0.7rem] md:text-[0.88rem]" style={{fontFamily:"'Space Grotesk', 'Outfit', sans-serif", letterSpacing:'0.01em'}}>Whatnot</div>
                  {/* Live status, under the title */}
                  {isLive ? (
                    <div className="flex items-center gap-1 text-red-500 font-semibold" style={{fontSize:'10px', lineHeight:1}}>
                      <span className="relative flex h-2 w-2 flex-shrink-0"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span></span>
                      LIVE NOW
                    </div>
                  ) : (
                    <span className="text-zinc-400 font-medium" style={{fontSize:'10px', lineHeight:1}}>Offline</span>
                  )}
                </div>
                {/* Stats from Whatnot, refreshed every 6 hours by the bot */}
                <div className="absolute right-2.5 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <div className="flex items-center gap-1 text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>
                    <Users className="h-[9px] w-[9px] flex-shrink-0" />
                    {whatnotStats?.followers != null ? whatnotStats.followers.toLocaleString() : '—'}
                  </div>
                  <div className="flex items-center gap-1 text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>
                    <Package className="h-[9px] w-[9px] flex-shrink-0" />
                    {whatnotStats?.sold != null ? whatnotStats.sold.toLocaleString() : '—'}
                  </div>
                  <div className="flex items-center gap-1 text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>
                    <Star className="h-[9px] w-[9px] flex-shrink-0" style={{color:'#fbbf24', fill:'#fbbf24'}} />
                    {whatnotStats?.rating ? whatnotStats.rating.toFixed(1) : '—'}
                  </div>
                </div>
              </button>

              {/* Discord */}
              <a href="https://cheeks.uk/docs" className="social-bar-press relative flex items-center gap-2 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-3 py-2.5 active:scale-[0.98] transition-all duration-200">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <div className="relative">
                      <img src="/discord.png" alt="Discord" className="w-7 h-7 object-contain" />
                      {voiceCount > 0 && (
                        <div className="absolute -top-1 -right-2 text-white px-1 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm" style={{backgroundColor:'#23a55a', border:'1.5px solid #18181b'}}>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="w-2 h-2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                          <span className="font-medium leading-none" style={{fontSize:'0.5rem'}}>{voiceCount}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.7rem] md:text-[0.88rem]" style={{fontFamily:"'Space Grotesk', 'Outfit', sans-serif", letterSpacing:'0.01em'}}>Discord</div>
                  <div className="absolute right-2.5 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                    <div className="flex items-center gap-1 text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>
                      <Users className="h-[9px] w-[9px] flex-shrink-0" />
                      {memberCount !== null ? formatCount(memberCount) : '—'}
                    </div>
                    <div className="flex items-center gap-1 font-medium" style={{fontSize:'9px', lineHeight:1, color:'#23a55a'}}>
                      <span className="inline-block rounded-full flex-shrink-0" style={{width:'5px', height:'5px', backgroundColor:'#23a55a'}}></span>
                      {onlineCount !== null ? onlineCount.toLocaleString() : '—'} online
                    </div>
                    {voiceCount > 0 ? (
                      <div className="flex items-center gap-1 font-medium" style={{fontSize:'9px', lineHeight:1, color:'#23a55a'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="9" height="9"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                        {voiceCount} in voice
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 font-medium" style={{fontSize:'9px', lineHeight:1, color:'#71717a'}}>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="9" height="9"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                        0 in voice
                      </div>
                    )}
                  </div>
                </a>

              {/* Instagram */}
              <a href="https://www.instagram.com/togecheeks" target="_blank" rel="noopener noreferrer" className="social-bar-press relative flex items-center gap-2 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-3 py-2.5 active:scale-[0.98] transition-all duration-200">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/instagram.png" alt="Instagram" className="w-7 h-7 object-contain" />
                </div>
                <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.7rem] md:text-[0.88rem]" style={{fontFamily:"'Space Grotesk', 'Outfit', sans-serif", letterSpacing:'0.01em'}}>Instagram</div>
                <div className="absolute right-2.5 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <div className="flex items-center gap-1 text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>
                    <Users className="h-[9px] w-[9px] flex-shrink-0" />
                    {instagramCount !== null ? formatCount(instagramCount) : '—'}
                  </div>
                  <div className="flex items-center gap-1 text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>
                    <Camera className="h-[9px] w-[9px] flex-shrink-0" />
                    201
                  </div>
                  <span className="text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>@togecheeks</span>
                </div>
              </a>

              {/* MakerWorld */}
              <a href="https://makerworld.com/en/@togecheeks/upload" target="_blank" rel="noopener noreferrer" className="social-bar-press relative flex items-center gap-2 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-3 py-2.5 active:scale-[0.98] transition-all duration-200">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center">
                    <img src="/makerworld.png" alt="MakerWorld" className="w-5 h-5 object-contain" />
                  </div>
                </div>
                <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.7rem] md:text-[0.88rem]" style={{fontFamily:"'Space Grotesk', 'Outfit', sans-serif", letterSpacing:'0.01em'}}>MakerWorld</div>
                <div className="absolute right-2.5 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <span className="text-white font-medium flex items-center gap-1" style={{fontSize:'9px', lineHeight:1}}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                    3.7M
                  </span>
                  <span className="text-white font-medium flex items-center gap-1" style={{fontSize:'9px', lineHeight:1}}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2v11zm19.83-7.12c.11-.25.17-.52.17-.8V11c0-1.1-.9-2-2-2h-5.5l.92-4.65c.05-.22.02-.46-.08-.66-.23-.45-.52-.86-.88-1.22L14 2 7.59 8.41C7.21 8.79 7 9.3 7 9.83V19c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.12z"/></svg>
                    3.2k
                  </span>
                  <span className="text-white font-medium flex items-center gap-1" style={{fontSize:'9px', lineHeight:1}}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M19 9h-4V3H9v6H5l7 7 7-7zm-14 9v2h14v-2H5z"/></svg>
                    2.9k
                  </span>
                </div>
              </a>

              {/* Pokémon GO */}
              <div className="social-bar-press relative flex items-center gap-2 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-3 py-2.5 cursor-pointer active:scale-[0.98] transition-all duration-200" onClick={() => { navigator.clipboard?.writeText('664463929998'); setShowPogoModal(true); setShowCopiedToast(true); setTimeout(() => setShowCopiedToast(false), 2500); }}>
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/pogo.png" alt="Pokémon GO" className="w-7 h-7 object-contain" />
                </div>
                <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.7rem] md:text-[0.88rem]" style={{fontFamily:"'Space Grotesk', 'Outfit', sans-serif", letterSpacing:'0.01em'}}>Pokémon GO</div>
                <div className="absolute right-2.5 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <span className="text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>Lv. 71</span>
                  <span className="text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>Valor</span>
                  <span className="text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>togecheeks</span>
                </div>
              </div>

              {/* Steam */}
              <a href="https://steamcommunity.com/id/togecheeks/" target="_blank" rel="noopener noreferrer" className="social-bar-press relative flex items-center gap-2 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-3 py-2.5 active:scale-[0.98] transition-all duration-200">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0">
                  <img src="/steam.png" alt="Steam" className="w-7 h-7 object-contain" />
                </div>
                <div className="text-white font-semibold leading-none uppercase tracking-widest text-[0.7rem] md:text-[0.88rem]" style={{fontFamily:"'Space Grotesk', 'Outfit', sans-serif", letterSpacing:'0.01em'}}>Steam</div>
                <div className="absolute right-2.5 top-0 bottom-0 flex flex-col justify-between items-end py-2">
                  <span className="text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>&nbsp;</span>
                  <span className="text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>&nbsp;</span>
                  <span className="text-white font-medium" style={{fontSize:'9px', lineHeight:1}}>cheeks</span>
                </div>
              </a>

            </div>

            {/* NEXT STREAMS: my next two scheduled shows (from the whatnotlive cog), big thumbnails with the details underneath.
                Tapping one opens it on Whatnot to bookmark. Hidden while I'm live, or if nothing's scheduled */}
            {!isLive && whatnotNextShow && (() => {
              const list = Array.isArray(whatnotNextShow.upcoming) && whatnotNextShow.upcoming.length ? whatnotNextShow.upcoming : [whatnotNextShow];
              const shows = list.filter(s => s && s.startAt > Date.now() - 2 * 60 * 60 * 1000).slice(0, 2);
              if (!shows.length) return null;
              const details = (s) => {
                const when = new Date(s.startAt).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                const mins = Math.ceil((s.startAt - Date.now()) / 60000);
                const days = Math.floor(mins / 1440);
                const countdown = mins <= 0 ? 'Starting soon'
                  : days >= 365 ? `in ${Math.floor(days / 365)} year${Math.floor(days / 365) !== 1 ? 's' : ''}`
                  : days >= 60 ? `in ${Math.floor(days / 30)} months`
                  : days >= 7 ? `in ${days} days`
                  : mins >= 1440 ? `in ${days}d ${Math.floor((mins % 1440) / 60)}h`
                  : mins >= 60 ? `in ${Math.floor(mins / 60)}h ${mins % 60}m` : `in ${mins}m`;
                return { when, countdown, title: (s.title || '').replace(/\s*\n\s*/g, ' ') };
              };
              return (
                <div className="bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-3 pt-2.5 pb-3 flex flex-col gap-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 font-bold uppercase" style={{fontSize:'0.65rem', letterSpacing:'0.04em', lineHeight:1, color:'#ffffff'}}>
                      <img src="/wnlogo.png" alt="" className="flex-shrink-0 object-contain" style={{width:'14px', height:'14px'}} />
                      {shows.length > 1 ? 'Next streams:' : 'Next stream:'}
                    </span>
                    <span className="text-zinc-400 font-medium text-right" style={{fontSize:'0.6rem', lineHeight:1.2}}>🔖 Tap to bookmark</span>
                  </div>
                  <div className={`grid gap-2.5 ${shows.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {shows.map((s) => {
                      const d = details(s);
                      return (
                        <a key={s.url || s.startAt} href={s.url} target="_blank" rel="noopener noreferrer" className={`group flex ${shows.length > 1 ? 'flex-col' : 'flex-row items-center'} gap-2 active:scale-[0.98] transition-all duration-200`}>
                          <div className="relative rounded-xl overflow-hidden border border-zinc-700/70 bg-black/60 group-hover:border-zinc-500/70 transition-colors flex-shrink-0" style={shows.length > 1 ? { aspectRatio: '3 / 4' } : { width: '96px', aspectRatio: '3 / 4' }}>
                            {s.thumbnail ? (
                              <img src={s.thumbnail} alt="" loading="lazy" referrerPolicy="no-referrer" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center text-zinc-600"><img src="/wnlogo.png" alt="" className="w-8 h-8 object-contain opacity-40" /></div>
                            )}
                            {/* Countdown on the image */}
                            <span className="absolute top-1.5 right-1.5 rounded-md px-1.5 py-0.5 font-bold" style={{fontSize:'0.58rem', lineHeight:1.2, background:'rgba(0,0,0,0.75)', color:'#fbe510', border:'1px solid rgba(251,229,16,0.35)'}}>⏳ {d.countdown}</span>
                          </div>
                          <div className="flex flex-col gap-1 min-w-0">
                            <p className="text-white font-semibold group-hover:underline" style={{fontSize:'0.72rem', lineHeight:1.25, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>{d.title}</p>
                            <span className="text-zinc-400 font-medium" style={{fontSize:'0.6rem', lineHeight:1.2}}>🕒 {d.when}</span>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* LIVE NOW + SCHEDULE banner: friends the Whatnot bot is tracking who are live, then
                today's upcoming shows (published by ?wnlive schedule). One thin panel per stream.
                Hidden while cheeks is live, so visitors aren't pointed elsewhere */}
            {!isLive && (() => {
              // Only today's (UK) schedule. A show that's already live is shown under LIVE NOW only,
              // never twice. A show that's past its start time but not live yet stays as "Starting soon"
              // for up to 2 hours (the bot re-syncs the schedule every 5 minutes anyway).
              const ukToday = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
              const liveUrls = new Set(whatnotLive.map(s => s.url));
              const seenShows = new Set(); // same seller at the same minute = duplicate placeholder, show once
              const upcomingShows = (whatnotSchedule?.date === ukToday ? (whatnotSchedule.shows || []) : [])
                .filter(s => s && !liveUrls.has(s.url) && s.startAt > Date.now() - 2 * 60 * 60 * 1000)
                .filter(s => {
                  const key = `${(s.username || '').toLowerCase()}|${Math.floor(s.startAt / 60000)}`;
                  if (seenShows.has(key)) return false;
                  seenShows.add(key);
                  return true;
                })
                .sort((a, b) => a.startAt - b.startAt);
              if (whatnotLive.length === 0 && upcomingShows.length === 0) return null;
              const offlineNote = (
                <span className="text-zinc-400 font-medium text-right" style={{fontSize:'0.65rem', lineHeight:1.2}}>
                  cheeks is offline — catch their friends instead!
                </span>
              );
              // Live streamers get a little red dot on their avatar
              const avatar = (s, live = false) => (
                <span className="relative flex-shrink-0" style={{width:'30px', height:'30px'}}>
                  {s.avatar ? (
                    <img src={s.avatar} alt="" loading="lazy" referrerPolicy="no-referrer" className="rounded-full object-cover border border-zinc-700/70" style={{width:'30px', height:'30px'}} onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                  ) : (
                    <span className="block rounded-full bg-zinc-800 border border-zinc-700/70" style={{width:'30px', height:'30px'}} />
                  )}
                  {live && (
                    <span className="absolute rounded-full" style={{width:'10px', height:'10px', right:'-1px', top:'-1px', background:'#ef4444', border:'2px solid #0a0a0a', boxShadow:'0 0 6px rgba(239,68,68,0.7)'}} />
                  )}
                </span>
              );
              // The stream's thumbnail, tilted at the end of the row: it pokes out above the row and its
              // bottom is cropped by the row's bottom edge (the frame clips below, not above)
              const thumb = (s) => s.thumbnail ? (
                <span className="absolute pointer-events-none" aria-hidden="true" style={{ right: '8px', top: '-14px', bottom: 0, width: '84px', overflow: 'hidden' }}>
                  <img src={s.thumbnail} alt="" loading="lazy" referrerPolicy="no-referrer" className="absolute object-cover rounded-md border"
                    // Same thin grey stroke as the scheduled rows, on live rows too
                    style={{ width: '42px', height: '62px', right: '11px', top: '5px', transform: 'rotate(8deg)', boxShadow: '0 4px 14px rgba(0,0,0,0.65)',
                             borderColor: 'rgba(63,63,70,0.7)' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                </span>
              ) : null;
              return (
              <div className="relative flex flex-col gap-1.5 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-3 py-2">
                {whatnotLive.length > 0 && (<>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 flex-shrink-0 text-white font-bold uppercase" style={{fontSize:'0.65rem', letterSpacing:'0.04em', lineHeight:1}}>
                    <img src="/wnlogo.png" alt="" className="flex-shrink-0 object-contain" style={{width:'14px', height:'14px'}} />
                    LIVE NOW:
                  </span>
                  {offlineNote}
                </div>
                <div className="flex flex-col gap-3 pt-2">
                  {whatnotLive.map(s => {
                    // How long they've been live (re-renders every second with the clock above)
                    const mins = s.startedAt ? Math.max(0, Math.floor((Date.now() - s.startedAt) / 60000)) : null;
                    const liveFor = mins === null ? null : mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
                    const compact = (n) => new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 }).format(n || 0);
                    return (
                      // Each live friend gets their own thin panel, styled like the trainer code box
                      <a key={s.username} href={s.url} target="_blank" rel="noopener noreferrer" className={`group relative flex items-center gap-2.5 min-w-0 rounded-xl border px-2.5 py-2 active:scale-[0.98] transition-all duration-200 hover:brightness-125 ${s.thumbnail ? 'pr-[68px]' : ''}`}
                        // A slight red tint marks live streamers, so they stand apart from the scheduled shows below
                        style={{ background: 'linear-gradient(90deg, rgba(239,68,68,0.14), rgba(0,0,0,0.6) 65%)', borderColor: 'rgba(239,68,68,0.45)', boxShadow: '0 0 12px rgba(239,68,68,0.12)' }}>
                        {avatar(s, true)}
                        <span className="flex flex-col gap-0.5 min-w-0">
                          <span className="flex items-center gap-1.5 min-w-0 group-hover:underline" style={{fontSize:'0.7rem', lineHeight:1.2}}>
                            <span className="text-white font-semibold flex-shrink-0">@{s.username}</span>
                            {s.title && <span className="text-zinc-300 truncate">{s.title}</span>}
                          </span>
                          <span className="flex items-center gap-2 text-zinc-400 font-medium flex-wrap" style={{fontSize:'0.6rem', lineHeight:1.2}}>
                            <span>👀 {(s.viewers || 0).toLocaleString()} watching</span>
                            {liveFor && <span>⏱️ {liveFor}</span>}
                            {s.followers > 0 && <span>👥 {compact(s.followers)} followers</span>}
                          </span>
                        </span>
                        {thumb(s)}
                      </a>
                    );
                  })}
                </div>
                </>)}

                {upcomingShows.length > 0 && (<>
                <div className={`flex items-center justify-between gap-2 ${whatnotLive.length > 0 ? 'mt-1' : ''}`}>
                  <span className="flex items-center gap-1.5 flex-shrink-0 font-bold uppercase" style={{fontSize:'0.65rem', letterSpacing:'0.04em', lineHeight:1, color:'#ffffff'}}>
                    <img src="/wnlogo.png" alt="" className="flex-shrink-0 object-contain" style={{width:'14px', height:'14px'}} />
                    SCHEDULE:
                  </span>
                  {whatnotLive.length === 0 && offlineNote}
                </div>
                <div className="flex flex-col gap-3 pt-2">
                  {upcomingShows.map(s => {
                    // Start time in the visitor's own time zone, plus a countdown (ticks with the clock above)
                    const time = new Date(s.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    const mins = Math.ceil((s.startAt - Date.now()) / 60000);
                    const countdown = mins <= 0 ? 'Starting soon' : mins >= 60 ? `in ${Math.floor(mins / 60)}h ${mins % 60}m` : `in ${mins}m`;
                    return (
                      <a key={`${s.username}-${s.startAt}`} href={s.url} target="_blank" rel="noopener noreferrer" className={`group relative flex items-center gap-2.5 min-w-0 bg-black/60 rounded-xl border border-zinc-700/70 px-2.5 py-2 hover:border-zinc-500/70 active:scale-[0.98] transition-all duration-200 ${s.thumbnail ? 'pr-[68px]' : ''}`}>
                        {avatar(s)}
                        <span className="flex flex-col gap-0.5 min-w-0">
                          <span className="flex items-center gap-1.5 min-w-0 group-hover:underline" style={{fontSize:'0.7rem', lineHeight:1.2}}>
                            <span className="text-white font-semibold flex-shrink-0">@{s.username}</span>
                            {s.title && <span className="text-zinc-300 truncate">{s.title}</span>}
                          </span>
                          <span className="flex items-center gap-2 text-zinc-400 font-medium flex-wrap" style={{fontSize:'0.6rem', lineHeight:1.2}}>
                            <span>🕒 {time}</span>
                            <span>⏳ {countdown}</span>
                          </span>
                        </span>
                        {thumb(s)}
                      </a>
                    );
                  })}
                </div>
                </>)}
              </div>
              );
            })()}

            {/* PRODUCT INFO & FIRMWARE UPDATES: the stand's full panel (the banner at the top of the page scrolls here) */}
            <div id="product-info" className="flex flex-col gap-2.5" style={{ scrollMarginTop: '12px' }}>
            <div className="h-px mx-1 mt-1 mb-1" style={{background: "rgba(161,161,170,0.25)"}} />
            <p className="text-white font-semibold uppercase px-1" style={{fontSize:'0.65rem', letterSpacing:'0.08em', lineHeight:1}}>Product info &amp; firmware updates</p>

            {/* The stand's photos, info, setup instructions and firmware updater (same panel as on Creations) */}
            {renderStandV2(DIGITAL_STAND_V2, { anchorId: 'digital-stand-info' })}
            </div>


            {/* Fun Fact Panel */}
            {funFact && (
              <div className="bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-4 py-3 flex items-start gap-3">
                <img src="/pball.svg" alt="" className="h-4 w-4 flex-shrink-0 mt-0.5 relative" style={{filter:'brightness(0) invert(1)', zIndex: 25}} />
                <p className="text-zinc-400 leading-relaxed relative" style={{fontSize:'0.7rem', zIndex: 25}}>{funFact}</p>
              </div>
            )}

          </div>
        </main>
      )}

      {/* ══════════════════════════════════════════
          INVENTORY TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'inventory' && (
        <main className="flex-1">
          <style>{`
            @keyframes inventoryFadeUp {
              from { opacity: 0; transform: translateY(14px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .inv-card {
              animation: inventoryFadeUp 0.35s ease both;
            }
          `}</style>

          {/* Grid */}
          <div className="max-w-7xl mx-auto px-3 py-4">

            {/* Inventory updating bar */}
            <div className="flex items-center gap-3 bg-zinc-900/40 border border-zinc-800/40 rounded-2xl px-4 py-2 mb-4">
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                <img src="/pball.svg" alt="" className="h-4 w-4 animate-spin" style={{animationDuration:"2s", filter:"brightness(0) invert(1)"}} />
              </div>
              <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
              <div>
                <p className="text-zinc-300 text-xs font-semibold leading-none">Inventory Updating...</p>
                <p className="text-zinc-400 text-[10px] mt-0.5 leading-none">New items are being added to this page shortly.</p>
              </div>
            </div>

            {filteredImages.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                {filteredImages.map((image, idx) => (
                  <div
                    key={image.id}
                    className="inv-card group bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm rounded-xl border border-zinc-700/70 hover:border-cyan-400/50 active:border-cyan-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-cyan-400/10 overflow-hidden cursor-pointer shadow-[0_4px_16px_rgba(0,0,0,0.25)] hover:-translate-y-0.5"
                    style={{animationDelay:`${Math.min(idx * 0.03, 0.6)}s`}}
                    onClick={() => setSelectedInventoryImage(image)}
                  >
                    <div className="aspect-square bg-zinc-800/50 flex items-center justify-center relative overflow-hidden">
                      <img
                        src={`/${image.folder}/${image.filename}`}
                        alt={image.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-600 to-slate-700 flex items-center justify-center" style={{ display: 'none' }}>
                        <div className="text-center p-4">
                          <div className="w-16 h-16 mx-auto mb-2 opacity-30">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-zinc-400">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2"/>
                              <circle cx="8.5" cy="8.5" r="1.5" strokeWidth="2"/>
                              <polyline points="21 15 16 10 5 21" strokeWidth="2"/>
                            </svg>
                          </div>
                          <p className="text-xs text-zinc-400 font-medium">Image not found</p>
                        </div>
                      </div>

                      {image.grade && (
                        <div className="absolute top-2 left-2 bg-gradient-to-r from-cyan-500 to-pink-600 text-white px-2 py-1 rounded-md text-xs font-bold shadow-lg backdrop-blur-sm border border-cyan-400/50">
                          {image.grade}
                        </div>
                      )}

                      {image.serial && (
                        <div className="absolute text-white font-bold tracking-widest shadow-lg" style={{bottom:'6px', left:'6px', fontSize:'0.6rem', background:'rgba(0,0,0,0.4)', backdropFilter:'blur(4px)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'5px', padding:'2px 6px', letterSpacing:'0.08em'}}>
                          {image.serial}
                        </div>
                      )}

                      {/* Team badge tab — sticks up from info bar, bottom right */}
                      {image.filename.includes('Crystal Palace') && (
                        <div className="absolute bottom-0" style={{right:'6px', zIndex:2}}>
                          <div className="flex items-center justify-center px-2 pt-1.5 pb-0 rounded-t-lg" style={{background:'rgba(0,0,0,0.85)', width:'36px'}}>
                            <img src="/cpfc.png" alt="CPFC" style={{width:'22px', height:'22px', objectFit:'contain'}} />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-semibold text-white mb-1 group-hover:text-cyan-400 transition-colors leading-tight">
                        {image.name}
                      </h3>
                      {image.set.split('  ').map((line, idx) => (
                        <p key={idx} className="text-[10px] text-zinc-400 leading-tight">{line}</p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-700/70 rounded-xl p-12 text-center">
                <p className="text-zinc-400">No items found matching your search.</p>
              </div>
            )}
          </div>
        </main>
      )}

      {/* ══════════════════════════════════════════
          CREATIONS TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'creations' && (
        <main className="flex-1">
          <style>{STAND_PANEL_CSS}</style>
          <svg width="0" height="0" style={{position:'absolute'}}>
            <defs>
              <filter id="epaper">
                <feColorMatrix type="saturate" values="0"/>
                <feComponentTransfer>
                  <feFuncR type="discrete" tableValues="0 0.42 0.78 1"/>
                  <feFuncG type="discrete" tableValues="0 0.42 0.78 1"/>
                  <feFuncB type="discrete" tableValues="0 0.42 0.78 1"/>
                </feComponentTransfer>
              </filter>
            </defs>
          </svg>

          <div className="max-w-2xl mx-auto px-4 pt-2 pb-6">
            <div className="flex flex-col gap-5">

            {/* Designed & manufactured bar */}
            <div className="flex items-center gap-2 bg-zinc-900/40 border border-zinc-800/40 rounded-2xl corner-glow px-3 py-2">
              <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                <img src="https://flagcdn.com/gb.svg" alt="UK" className="w-5 h-5 rounded-full object-cover" style={{aspectRatio:'1/1'}} />
              </div>
              <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
              <div className="min-w-0">
                <p className="text-white font-semibold leading-none" style={{fontSize: 'clamp(0.58rem, 3vw, 0.75rem)', whiteSpace: 'nowrap'}}>Designed and manufactured by CHEEKS in the United Kingdom</p>
              </div>
            </div>

            {/* ── CREATION CARD ── */}
            {renderStandV2(DIGITAL_STAND_V2)}

            {/* ── V3 CARD ── */}
            <div id="v3-card" className="creation-card bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] overflow-hidden">
              {/* Product image — placeholder */}
              <div className="relative w-full overflow-hidden flex items-center justify-center glow-neutral" style={{aspectRatio:'4/3', background:'radial-gradient(ellipse at 60% 50%, rgba(4,175,247,0.08) 0%, rgba(244,42,228,0.06) 40%, rgba(0,0,0,0.0) 70%)'}}>
                <svg style={{position:'absolute',inset:0,width:'100%',height:'100%',zIndex:0}} viewBox="0 0 600 450" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                  <defs>
                    <pattern id="cgridv3" width="40" height="40" patternUnits="userSpaceOnUse">
                      <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(4,175,247,0.18)" strokeWidth="0.5"/>
                    </pattern>
                  </defs>
                  <rect width="600" height="450" fill="url(#cgridv3)"/>
                </svg>
                <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{background:'linear-gradient(to top, rgba(244,42,228,0.06), transparent)', zIndex:0}} />
                {/* Centre placeholder — spinning pokeball + text */}
                <div className="flex flex-col items-center justify-center gap-3" style={{position:'relative', zIndex:30}}>
                  <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                    <img src="/pball.svg" alt="" className="h-6 w-6 animate-spin" style={{animationDuration:'2s', filter:'brightness(0) invert(1)'}} />
                  </div>
                  <span className="text-white font-semibold tracking-wide" style={{fontSize:'0.85rem'}}>Image Coming Soon</span>
                </div>
                {/* Full Colour pill — top right */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #04aff7, #f42ae4)', zIndex:30}}>
                  Full Colour
                </div>
                {/* V3 version badge — bottom left */}
                <div className="absolute bottom-3 left-3 flex items-center justify-center rounded-full text-white font-bold text-[11px]" style={{width:'28px', height:'28px', background:'linear-gradient(135deg,#04aff7,#f42ae4)', zIndex:3, boxShadow:'0 0 8px rgba(4,175,247,0.4)'}}>
                  V3
                </div>
                {/* Mini preview placeholder — top left (600×400 ratio, taller than V2) */}
                <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5" style={{zIndex:30}}>
                  <div className="rounded-lg overflow-hidden border border-zinc-700/50 flex items-center justify-center" style={{width:'99px', height:'66px', background:'#000000'}}>
                    <div className="flex flex-col items-center gap-1 opacity-40">
                      <img src="/pball.svg" alt="" className="h-5 w-5 animate-spin" style={{animationDuration:'2s', filter:'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(400%) hue-rotate(160deg)'}} />
                    </div>
                  </div>
                  <div className="w-full text-center px-2 py-0.5 rounded-full text-[9px] font-semibold text-white/70" style={{background:'rgba(0,0,0,0.75)'}}>
                    Example content
                  </div>
                </div>
                {/* Thumbnail placeholders — bottom right */}
                <div className="absolute bottom-3 right-3 flex items-center gap-2" style={{zIndex:30}}>
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="rounded-lg overflow-hidden flex items-center justify-center"
                      style={{
                        width: '44px',
                        height: '44px',
                        background: '#000000',
                        border: '2px solid rgba(255,255,255,0.15)',
                      }}
                    >
                      <img src="/pball.svg" alt="" className="h-5 w-5 animate-spin" style={{animationDuration:'2s', filter:'brightness(0) saturate(100%) invert(70%) sepia(50%) saturate(400%) hue-rotate(160deg)', opacity:0.35}} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Info block */}
              <div className="px-4 pt-4 pb-3 relative edge-glow-top-thin edge-glow-bold">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-white font-bold text-base leading-tight">Digital Display Card &amp; Slab Stand V3</h2>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    'Full Colour',
                    'Wireless Control',
                    'Low Power',
                    'Stream Deck Ready',
                    'Easy Setup',
                    'No App Required',
                    'Plug & Play',
                    'Always-On Display',
                  ].map((point) => (
                    <div key={point} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                      <span className="text-white font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                    </div>
                  ))}
                </div>
                <p className="text-zinc-400 text-xs leading-relaxed">A wirelessly controlled display stand with a full colour screen, designed for graded slabs and raw cards from all major TCGs — Pokémon, MTG, Yu-Gi-Oh!, Disney, Sports and more. Change what's shown on screen from any device on your Wi-Fi: custom graphics, shout-outs, offers, discount codes, or stream overlays. Stream Deck compatible. The screen holds its image without power — and is only needed when updating the display.</p>
              </div>

              {/* Divider + Setup Instructions (greyed - coming soon) */}
              <div className="edge-glow-top-thin relative" style={{zIndex: 30}} />
              <div className="relative px-4 py-3 pointer-events-none select-none glow-neutral">
                <div className="absolute inset-0 bg-black/60 rounded-none flex items-center justify-center">
                  <span className="text-white text-[10px] font-semibold uppercase tracking-widest flex items-center gap-1.5">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Updating...
                  </span>
                </div>
                <div className="w-full flex items-center justify-between opacity-30">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'#27272a'}}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                      </svg>
                    </div>
                    <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
                    <div className="text-left">
                      <span className="text-xs font-semibold text-white" style={{display:'block', lineHeight:1, marginBottom:'3px'}}>Setup Instructions</span>
                      <p className="text-zinc-400" style={{fontSize:'0.6rem', lineHeight:1, margin:0}}>Follow these steps to connect your device and get it ready.</p>
                    </div>
                  </div>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'#27272a'}}>
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>
                </div>
              </div>
              <div className="edge-glow-top-thin relative" style={{zIndex: 30}} />

              {/* Update Firmware (greyed - coming soon) */}
              <div className="relative px-4 py-3 pointer-events-none select-none glow-neutral">
                <div className="absolute inset-0 bg-black/60 rounded-none flex items-center justify-center">
                  <span className="text-white text-[10px] font-semibold uppercase tracking-widest flex items-center gap-1.5">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Updating...
                  </span>
                </div>
                <div className="w-full flex items-center justify-between opacity-30">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'#27272a'}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                      </svg>
                    </div>
                    <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
                    <div>
                      <div style={{display:'flex', alignItems:'center', gap:'6px', marginBottom:'3px'}}>
                        <span className="text-xs font-semibold text-white" style={{display:'block', lineHeight:1}}>Update Firmware</span>
                        <span className="font-mono" style={{fontSize:'0.55rem', color:'#ffffff', lineHeight:1}}>Latest: v1.2.0</span>
                      </div>
                      <p className="text-zinc-400" style={{fontSize:'0.6rem', lineHeight:1, margin:0}}>Keep your device up to date with the latest features.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'#27272a'}}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="edge-glow-top-thin relative" style={{zIndex: 30}} />

            </div>

            {/* ── ADJUSTABLE STAND CARD ── */}
            <div className="creation-card bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] overflow-hidden">
              <div className="relative w-full overflow-hidden glow-neutral" style={{aspectRatio:'1/1', background:'radial-gradient(ellipse at 60% 50%, rgba(4,175,247,0.08) 0%, rgba(244,42,228,0.06) 40%, rgba(0,0,0,0.0) 70%)'}}>
                <div className="absolute inset-0" style={{backgroundImage:'linear-gradient(rgba(4,175,247,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(4,175,247,0.04) 1px, transparent 1px)', backgroundSize:'32px 32px', zIndex:0}} />
                <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{background:'linear-gradient(to top, rgba(244,42,228,0.06), transparent)', zIndex:0}} />
                <img src="/stand_adjustable.jpeg" alt="Custom Card & Slab Stand (Adjustable)" className="w-full h-full object-cover" style={{position:'relative', zIndex:1}} />
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #04aff7, #f42ae4)'}}>
                  New
                </div>
              </div>
              <div className="px-4 pt-4 pb-3 relative edge-glow-top-thin edge-glow-bold">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-white font-bold text-base leading-tight">Custom Card & Slab Stand (Adjustable)</h2>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['3D Printed','Adjustable Fit','All TCGs','Slab & Raw Cards','Custom Colours'].map((point) => (
                    <div key={point} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <span className="text-white font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                    </div>
                  ))}
                </div>
                <p className="text-zinc-400 leading-relaxed mb-0" style={{fontSize:'0.75rem'}}>
                  A 3D printed display stand with an adjustable cradle to fit cards and slabs of varying sizes. Compatible with all major TCGs — Pokémon, MTG, Yu-Gi-Oh! and more. Print in your choice of filament colour to match your setup.
                </p>
              </div>
            </div>

            {/* ── FIXED STAND CARD ── */}
            <div className="creation-card bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] overflow-hidden">
              <div className="relative w-full overflow-hidden glow-neutral" style={{aspectRatio:'1/1', background:'radial-gradient(ellipse at 60% 50%, rgba(4,175,247,0.08) 0%, rgba(244,42,228,0.06) 40%, rgba(0,0,0,0.0) 70%)'}}>
                <div className="absolute inset-0" style={{backgroundImage:'linear-gradient(rgba(4,175,247,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(4,175,247,0.04) 1px, transparent 1px)', backgroundSize:'32px 32px', zIndex:0}} />
                <div className="absolute bottom-0 left-0 right-0 h-1/2" style={{background:'linear-gradient(to top, rgba(244,42,228,0.06), transparent)', zIndex:0}} />
                <img src="/stand_fixed.jpeg" alt="Custom Card & Slab Stand (Fixed)" className="w-full h-full object-cover" style={{position:'relative', zIndex:1}} />
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #04aff7, #f42ae4)'}}>
                  New
                </div>
              </div>
              <div className="px-4 pt-4 pb-3 relative edge-glow-top-thin edge-glow-bold">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-white font-bold text-base leading-tight">Custom Card & Slab Stand (Fixed)</h2>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['3D Printed','Fixed Cradle','All TCGs','Slab & Raw Cards','Custom Colours'].map((point) => (
                    <div key={point} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </div>
                      <span className="text-white font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                    </div>
                  ))}
                </div>
                <p className="text-zinc-400 leading-relaxed mb-0" style={{fontSize:'0.75rem'}}>
                  A clean, minimal 3D printed display stand with a fixed cradle — perfectly sized for your cards or slabs. Solid, sturdy and simple. Compatible with all major TCGs and available in any filament colour you like.
                </p>
              </div>
            </div>

            {/* ── RUGGED BOX CARD ── */}
            <div className="creation-card bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] overflow-hidden">

              {/* Product image */}
              <div className="relative w-full overflow-hidden glow-neutral" style={{aspectRatio:'4/3'}}>
                <img
                  src="/ruggedbox.jpg"
                  alt="Semi Rigid Card Sleeve Rugged Box"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold text-white" style={{background:'linear-gradient(135deg, #04aff7, #f42ae4)'}}>
                  Free Model
                </div>
              </div>

              {/* Info block */}
              <div className="px-4 pt-4 pb-3 relative edge-glow-top-thin edge-glow-bold">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="text-white font-bold text-base leading-tight">Semi Rigid Card Sleeve Rugged Box</h2>
                </div>

                {/* Feature pills */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    'Free 3D Print',
                    '100–120 Cards',
                    'Semi Rigid Sleeves',
                    'PLA or PETG',
                    'Snap-Shut Lid',
                  ].map((point) => (
                    <div key={point} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      </div>
                      <span className="text-white font-medium" style={{fontSize:'0.7rem'}}>{point}</span>
                    </div>
                  ))}
                </div>

                {/* Description */}
                <p className="text-zinc-400 leading-relaxed mb-4" style={{fontSize:'0.75rem'}}>
                  Designed to fit semi rigid sleeves perfectly and holds 100–120 sleeved cards. Prints well in PLA, PLA Matte or PETG if you want a bit more durability. Secure the lid with M3 screws and locking fasteners, or use 3mm rod / filament pins instead. Just avoid matte filament for the pins — they're prone to snapping.
                </p>

              </div>

              {/* MakerWorld download bar */}
              <div className="edge-glow-top-thin relative" />
              <a
                href="https://makerworld.com/en/models/1573714-semi-rigid-card-sleeve-rugged-box"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)'}}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </div>
                  <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
                  <div>
                    <span className="text-xs font-semibold text-white" style={{display:'block', lineHeight:1, marginBottom:'3px'}}>Download FREE on MakerWorld</span>
                    <p className="text-zinc-400" style={{fontSize:'0.6rem', lineHeight:1, margin:0}}>Get the free 3D print file for this model.</p>
                  </div>
                </div>
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#4ade80,#22c55e)'}}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </a>
              <div className="edge-glow-top-thin relative" />
            </div>

            {/* More creations coming soon */}
            <div className="flex items-center gap-3 bg-zinc-900/40 border border-zinc-800/40 rounded-2xl corner-glow px-4 py-2">
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                <img src="/pball.svg" alt="" className="h-4 w-4 animate-spin" style={{animationDuration:"2s", filter:"brightness(0) invert(1)"}} />
              </div>
              <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
              <div>
                <p className="text-white text-xs font-semibold" style={{lineHeight:1, marginBottom:'3px'}}>More creations on the way</p>
                <p className="text-zinc-400 text-[10px]" style={{lineHeight:1, margin:0}}>New products are being added to this page shortly.</p>
              </div>
            </div>

          </div>
        </div>
        </main>
      )}

      {/* ══════════════════════════════════════════
          SHOP TAB: my Whatnot listings (kept up to date by the whatnotlive cog)
      ══════════════════════════════════════════ */}
      {(activeTab === 'shop' || shopOpened) && (() => {
        const shopUrl = whatnotShop?.shopUrl || 'https://www.whatnot.com/user/togecheeks/shop';
        const listings = whatnotShop?.listings || [];
        const words = shopSearch.toLowerCase().split(/\s+/).filter(Boolean);
        const shown = words.length
          ? listings.filter(l => {
              const text = [l.title, l.subtitle, l.price, l.type].filter(Boolean).join(' ').toLowerCase();
              return words.every(w => text.includes(w));
            })
          : listings;
        return (
        <main className="flex-1" style={activeTab === 'shop' ? undefined : { display: 'none' }}>
          <div className="max-w-2xl mx-auto px-4 pt-2 pb-6">
            <div className="flex flex-col gap-4">
              {/* Header */}
              <div className="flex items-center justify-between gap-3 bg-gradient-to-br from-zinc-800/85 to-black/90 backdrop-blur-sm border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] px-4 py-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <img src="/wnlogo.png" alt="Whatnot" className="w-7 h-7 object-contain flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-white font-semibold uppercase tracking-widest leading-none" style={{fontSize:'0.8rem', fontFamily:"'Space Grotesk', 'Outfit', sans-serif"}}>Shop</p>
                    <p className="text-zinc-400 font-medium mt-1" style={{fontSize:'0.65rem', lineHeight:1}}>
                      {whatnotShop ? `${listings.length} item${listings.length !== 1 ? 's' : ''} for sale on Whatnot` : 'Loading…'}
                    </p>
                  </div>
                </div>
                <a href={shopUrl} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold active:scale-95 transition-all" style={{background:'#fbe510', color:'#000'}}>
                  View on Whatnot
                </a>
              </div>

              {/* Search */}
              {listings.length > 0 && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
                  <input
                    type="text"
                    enterKeyHint="search"
                    value={shopSearch}
                    onChange={(e) => setShopSearch(e.target.value)}
                    placeholder={`Search ${listings.length} item${listings.length !== 1 ? 's' : ''}…`}
                    aria-label="Search the shop"
                    className="w-full rounded-xl bg-black/60 border border-zinc-700/70 focus:border-zinc-500 outline-none text-white placeholder-zinc-500 pl-9 pr-9 py-2.5 text-sm transition-colors"
                    style={{ WebkitAppearance: 'none' }}
                  />
                  {shopSearch && (
                    <button onClick={() => setShopSearch('')} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-zinc-400 hover:text-white">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              )}

              {/* Products */}
              {listings.length > 0 && shown.length === 0 && (
                <div className="bg-gradient-to-br from-zinc-800/85 to-black/90 border border-zinc-700/70 rounded-2xl px-4 py-6 text-center">
                  <p className="text-white text-sm font-semibold">No items match "{shopSearch.trim()}"</p>
                  <p className="text-zinc-400 text-xs mt-1">Try a different word, or <button onClick={() => setShopSearch('')} className="text-cyan-400 underline">clear the search</button>.</p>
                </div>
              )}
              {whatnotShop && listings.length === 0 && (
                <div className="bg-gradient-to-br from-zinc-800/85 to-black/90 border border-zinc-700/70 rounded-2xl px-4 py-6 text-center">
                  <p className="text-white text-sm font-semibold">Nothing for sale right now</p>
                  <p className="text-zinc-400 text-xs mt-1">New items are added often, so check back soon!</p>
                </div>
              )}
              {shown.length > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {shown.map((l, i) => (
                    <a
                      key={l.id}
                      href={l.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="creation-card group flex flex-col bg-gradient-to-br from-zinc-800/85 to-black/90 border border-zinc-700/70 rounded-2xl overflow-hidden shadow-[0_6px_24px_rgba(0,0,0,0.5)] hover:border-zinc-500/70 active:scale-[0.98] transition-all duration-200"
                      style={{ animationDelay: `${Math.min(i, 10) * 0.04}s` }}
                    >
                      <div className="relative w-full bg-black/60" style={{ aspectRatio: '1 / 1' }}>
                        {l.image ? (
                          <img src={l.image} alt={l.title} loading="lazy" referrerPolicy="no-referrer" className="absolute inset-0 w-full h-full object-cover" />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center text-zinc-600"><Package className="h-8 w-8" /></div>
                        )}
                        {l.quantity > 1 && (
                          <span className="absolute top-2 right-2 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white" style={{background:'rgba(0,0,0,0.7)'}}>x{l.quantity}</span>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 px-2.5 py-2 flex-1">
                        {/* One line here (cards are narrow on mobile), so "Card/Slab Stand x5 ➜ Adjustable" */}
                        <p className="text-white font-semibold group-hover:underline" style={{fontSize:'0.72rem', lineHeight:1.25, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden'}}>{(l.title || '').replace(/\s*\n\s*/g, ' ')}</p>
                        {l.subtitle && <p className="text-zinc-400 truncate" style={{fontSize:'0.62rem', lineHeight:1.2}}>{l.subtitle}</p>}
                        <div className="flex items-center justify-between gap-2 mt-auto pt-1">
                          <span className="text-white font-bold" style={{fontSize:'0.85rem'}}>{l.price}</span>
                          <span className="text-zinc-400 font-medium flex-shrink-0" style={{fontSize:'0.58rem'}}>{l.type}</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
        );
      })()}

      {/* ══════════════════════════════════════════
          BATTLE TAB
      ══════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════
          CARD BUILDER TAB
      ══════════════════════════════════════════ */}
      {activeTab === 'builder' && (
        <main className="flex-1 flex flex-col">
          {!discordUser ? (
            <div className="relative flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
              <div className="fixed left-0 right-0 bottom-0 pointer-events-none" style={{ top: '100px', background: 'linear-gradient(160deg, rgba(4,175,247,0.08), rgba(0,0,0,0.4))' }} />
              <img src="/pball.svg" alt="" className="relative z-10 h-12 w-12 animate-spin" style={{ animationDuration: '2s' }} />
              <div className="relative z-10">
                <h2 className="text-white font-bold text-xl">Card Builder</h2>
                <p className="text-white text-sm mt-1">Design your own custom cards to print live on stream!</p>
              </div>
              <div className="relative z-10 flex flex-col gap-1 rounded-xl px-4 py-3 border border-zinc-800/40 backdrop-blur-sm" style={{ background: 'rgba(0,0,0,0.45)' }}>
                <p className="text-white text-sm">You need to log in with Discord to design a card!</p>
                <button
                  onClick={handleDiscordLogin}
                  className="flex items-center justify-center gap-1.5 text-white font-semibold text-xs rounded-lg px-4 py-2 transition-all duration-200 active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #04aff7, #f42ae4)' }}
                >
                  <LogIn className="h-3.5 w-3.5 flex-shrink-0" />
                  Log in with Discord
                </button>
              </div>
            </div>
          ) : (
          <div className="max-w-5xl mx-auto px-4 py-6 w-full">

            {/* Info bar */}
            <div className="flex items-center gap-2 bg-zinc-900/40 border border-zinc-800/40 rounded-2xl corner-glow px-3 py-2 mb-5">
              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}>
                <img src="/pball.svg" alt="" className="h-4 w-4 animate-spin" style={{animationDuration:'2s', filter:'brightness(0) invert(1)'}} />
              </div>
              <div className="w-px h-6 flex-shrink-0" style={{background: "rgba(161,161,170,0.4)"}} />
              <div>
                <p className="text-white text-xs font-semibold leading-none">Design your own custom card</p>
                <p className="text-white text-[10px] mt-0.5 leading-none">Made live by CHEEKS on Whatnot</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

              {/* ── Live preview ── */}
              <div className="flex flex-col items-center gap-3 lg:sticky lg:top-24">
                <style>{`
                  @keyframes cardIdleSheen {
                    0%, 100% { background-position: 0% 50%; }
                    50%      { background-position: 100% 50%; }
                  }
                  .builder-card-sheen {
                    background-image: repeating-linear-gradient(115deg,
                      rgba(255,255,255,0) 0%, rgba(255,255,255,0.55) 3%, rgba(255,255,255,0) 6%,
                      rgba(255,255,255,0) 30%, rgba(255,255,255,0.4) 34%, rgba(255,255,255,0) 38%);
                    background-size: 250% 250%;
                    animation: cardIdleSheen 5s ease-in-out infinite;
                    mix-blend-mode: overlay;
                  }
                `}</style>
                <div style={{ perspective: '900px' }}>
                  <div
                    onMouseMove={handleBuilderCardMouseMove}
                    onMouseLeave={handleBuilderCardMouseLeave}
                    className="relative w-full"
                    style={{
                      width: 'min(78vw, 300px)',
                      aspectRatio: '5/7',
                      borderRadius: '18px',
                      transform: `rotateX(${builderTilt.rx}deg) rotateY(${builderTilt.ry}deg) scale(${builderTilt.active ? 1.035 : 1})`,
                      transition: builderTilt.active ? 'transform 0.06s linear, box-shadow 0.2s ease' : 'transform 0.6s cubic-bezier(0.23,1,0.32,1), box-shadow 0.4s ease',
                      boxShadow: builderTilt.active
                        ? `${-builderTilt.ry * 1.2}px ${builderTilt.rx * 1.2 + 18}px 34px -10px rgba(0,0,0,0.55), 0 0 26px ${selectedBuilderType.color}66`
                        : '0 14px 28px -10px rgba(0,0,0,0.55)',
                      // White outer frame — same across every type/theme
                      background: `radial-gradient(circle at 28% 18%, rgba(255,255,255,0.5), transparent 55%), linear-gradient(160deg, rgba(0,0,0,0.15), transparent 65%), #ffffff`,
                      padding: '7px',
                    }}
                  >
                    {/* Inner card body */}
                    <div className="relative w-full h-full rounded-[12px] overflow-hidden flex flex-col" style={{ background: selectedBuilderBackground.gradient }}>

                      {/* Holo overlays */}
                      <div className="absolute inset-0 pointer-events-none builder-card-sheen" style={{ opacity: builderTilt.active ? 0.15 : 0.55 }} />
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: `radial-gradient(circle at ${builderTilt.mx}% ${builderTilt.my}%, rgba(255,255,255,0.85), transparent 45%)`,
                          mixBlendMode: 'overlay',
                          opacity: builderTilt.active ? 0.9 : 0,
                          transition: 'opacity 0.25s ease',
                        }}
                      />
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          backgroundImage: 'repeating-linear-gradient(115deg, #ff8a8a 0%, #ffe38a 12%, #8affab 24%, #8adfff 36%, #c28aff 48%, #ff8a8a 60%)',
                          backgroundSize: '220% 220%',
                          backgroundPosition: `${builderTilt.mx}% ${builderTilt.my}%`,
                          mixBlendMode: 'color-dodge',
                          opacity: builderTilt.active ? 0.4 : 0,
                          transition: 'opacity 0.25s ease',
                        }}
                      />

                      {/* Content */}
                      <div className="relative flex flex-col h-full px-2.5 pt-2 pb-2" style={{ fontFamily: "'Barlow Condensed', 'Arial Narrow', sans-serif" }}>

                        {/* Stage pill */}
                        <div className="flex items-center justify-between">
                          <span
                            className="font-bold uppercase text-zinc-900/70"
                            style={{ fontSize: '0.42rem', letterSpacing: '0.1em', background: 'rgba(255,255,255,0.5)', borderRadius: '999px', padding: '1.5px 6px' }}
                          >
                            Basic
                          </span>
                          <span
                            className="font-bold text-zinc-900"
                            style={{ fontSize: '0.5rem', letterSpacing: '0.08em', background: 'rgba(255,255,255,0.5)', borderRadius: '999px', padding: '1.5px 6px' }}
                          >
                            {selectedBuilderType.id.toUpperCase()} TYPE
                          </span>
                        </div>

                        {/* Name + HP row */}
                        <div className="flex items-center justify-between gap-1.5 mt-1">
                          <span
                            className="font-extrabold text-zinc-900 truncate flex-1 min-w-0"
                            style={{
                              fontSize: '1.1rem',
                              letterSpacing: '-0.01em',
                              textShadow: '0.5px 0.5px 0 rgba(255,255,255,0.5), -0.5px 0 0 rgba(0,0,0,0.25), 0 1.5px 1px rgba(0,0,0,0.2)',
                            }}
                          >
                            {builderCardName || 'Card Name'}
                          </span>
                          <span className="flex items-center gap-1 flex-shrink-0 font-extrabold text-zinc-900 ml-auto" style={{ fontSize: '1rem' }}>
                            <span style={{ fontSize: '0.65em' }}>HP</span>
                            <span style={{ fontSize: '1em', lineHeight: 1 }}>{builderHp || '0'}</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.35))' }}>
                              <circle cx="12" cy="12" r="11" fill={selectedBuilderType.color} stroke="rgba(0,0,0,0.3)" strokeWidth="0.75" />
                              {typeIconGlyph(selectedBuilderType.id)}
                            </svg>
                          </span>
                        </div>

                        {/* Character artwork — scene art (if picked) fills just this box, behind the Pokémon */}
                        <div
                          className="relative mt-1.5 flex-1 rounded-lg overflow-hidden flex items-center justify-center"
                          style={{
                            background: builderScene === 'none' ? 'radial-gradient(circle at 50% 30%, rgba(255,255,255,0.18), rgba(0,0,0,0.35) 70%)' : undefined,
                          }}
                        >
                          {builderScene !== 'none' && (
                            <>
                              <div className="absolute inset-0">{sceneArt(builderScene)}</div>
                              <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% 30%, rgba(255,255,255,0.06), rgba(0,0,0,0.3) 78%)' }} />
                            </>
                          )}
                          {/* Inner border/shadow frame — its own layer so it always sits above any scene art; rounded to match the box */}
                          <div className="absolute inset-0 rounded-lg pointer-events-none" style={{ boxShadow: 'inset 0 0 0 1.5px rgba(255,255,255,0.5), inset 0 6px 14px rgba(0,0,0,0.3)' }} />
                          {builderSelectedPokemon ? (
                            <img
                              src={pokemonSpriteUrl(builderSelectedPokemon.id)}
                              alt={builderSelectedPokemon.name}
                              className="w-full h-full object-contain p-2"
                              style={{ filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.45))' }}
                              draggable={false}
                            />
                          ) : (
                            <div className="flex flex-col items-center gap-2 opacity-60">
                              <img src="/pball.svg" alt="" className="h-8 w-8 animate-spin" style={{ animationDuration: '2s' }} />
                              <span className="text-white font-semibold" style={{ fontSize: '0.65rem' }}>Select a Pokémon</span>
                            </div>
                          )}
                        </div>

                        {/* Attack row — text stacks on the left, damage sits big & vertically centered on the right */}
                        <div className="mt-1.5 rounded-lg px-2.5 py-2 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.9)', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)' }}>
                          <div className="flex-1 min-w-0 flex flex-col justify-center gap-0.5">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <svg width="17" height="17" viewBox="0 0 24 24" fill="white" className="flex-shrink-0">
                                <circle cx="12" cy="12" r="11" fill={selectedBuilderType.color} stroke="rgba(0,0,0,0.25)" strokeWidth="0.75" />
                                {typeIconGlyph(selectedBuilderType.id)}
                              </svg>
                              <span className="font-extrabold text-zinc-900 truncate" style={{ fontSize: '1rem' }}>{builderMoveName || 'Move Name'}</span>
                            </div>
                            <p className="text-zinc-800 font-medium leading-snug" style={{ fontSize: '0.78rem' }}>
                              {builderMoveDesc || 'Move description goes here.'}
                            </p>
                          </div>
                          <span
                            className="font-extrabold text-zinc-900 flex-shrink-0 text-right"
                            style={{ fontSize: '1.4rem', lineHeight: 1, textShadow: '0 1px 0 rgba(255,255,255,0.4)' }}
                          >
                            {builderMoveName ? builderDisplayDamage : ''}
                          </span>
                        </div>

                        {/* Card number footer */}
                        <div className="flex items-center justify-end mt-1.5 px-0.5">
                          <span className="font-semibold text-white/80" style={{ fontSize: '0.48rem', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>{builderDisplayCardNumber}</span>
                        </div>

                        {/* Footer watermark */}
                        <p className="text-center mt-0.5 font-semibold text-white/70" style={{ fontSize: '0.42rem', letterSpacing: '0.1em', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                          © CHEEKS 2026
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-zinc-400 text-[10px]">Tilt with your cursor · updates live as you design</p>
              </div>

              {/* ── Controls ── */}
              <div className="flex flex-col gap-5">

                {/* Scene picker */}
                <div>
                  <label className="text-zinc-300 text-xs font-semibold block mb-2">Background scene</label>
                  <div className="grid grid-cols-5 gap-2">
                    {cardScenes.map(scene => (
                      <button
                        key={scene.id}
                        onClick={() => setBuilderScene(scene.id)}
                        className="rounded-lg overflow-hidden border-2 transition-all duration-150 flex flex-col items-center"
                        style={{
                          borderColor: builderScene === scene.id ? '#04aff7' : 'rgba(0,0,0,0.75)',
                          boxShadow: builderScene === scene.id ? '0 0 0 2px rgba(4,175,247,0.3)' : 'none',
                        }}
                      >
                        <div className="relative w-full overflow-hidden" style={{ height: '32px', background: selectedBuilderBackground.gradient }}>
                          {scene.id !== 'none' && sceneArt(scene.id)}
                        </div>
                        <span className="text-[8px] font-semibold text-zinc-300 py-1 truncate px-0.5">{scene.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pokemon picker */}
                <div className="relative" data-pokemon-picker>
                  <label className="text-zinc-300 text-xs font-semibold block mb-2">Character (Pokémon)</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder={builderSelectedPokemon ? builderSelectedPokemon.name : 'Search for a Pokémon…'}
                      value={builderPokemonSearch}
                      onChange={(e) => { setBuilderPokemonSearch(e.target.value); setBuilderShowPokemonPicker(true); }}
                      onFocus={() => setBuilderShowPokemonPicker(true)}
                      className="pl-9 pr-3 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-sm transition-all duration-200"
                    />
                  </div>
                  {builderSelectedPokemon && (
                    <div className="flex items-center gap-2 mt-2 bg-gradient-to-br from-zinc-800/85 to-black/90 border border-zinc-700/70 rounded-lg px-2.5 py-1.5">
                      <img src={pokemonSpriteUrl(builderSelectedPokemon.id)} alt="" className="h-6 w-6 object-contain" />
                      <span className="text-white text-xs font-semibold capitalize">{builderSelectedPokemon.name}</span>
                    </div>
                  )}
                  {builderSelectedPokemon && builderSelectedPokemon.id !== 'togecheeks' && (
                    <div className="flex items-center gap-1.5 mt-2">
                      {spriteStyles.map(style => (
                        <button
                          key={style.id}
                          onClick={() => setBuilderSpriteStyle(style.id)}
                          className="flex-1 rounded-md py-1 text-[10px] font-semibold transition-all duration-150"
                          style={{
                            background: builderSpriteStyle === style.id ? 'linear-gradient(135deg,#04aff7,#f42ae4)' : 'rgba(39,39,42,0.5)',
                            color: builderSpriteStyle === style.id ? 'white' : 'rgba(161,161,170,0.9)',
                          }}
                        >
                          {style.name}
                        </button>
                      ))}
                    </div>
                  )}
                  {builderShowPokemonPicker && builderPokemonSearch.trim() && (
                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl">
                      {filteredBuilderPokemon.length > 0 ? (
                        filteredBuilderPokemon.map(p => (
                          <button
                            key={p.id}
                            onClick={() => {
                              setBuilderSelectedPokemon(p);
                              setBuilderPokemonSearch('');
                              setBuilderShowPokemonPicker(false);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-zinc-800/60 transition-colors text-left"
                          >
                            <img src={pokemonSpriteUrl(p.id)} alt="" className="h-7 w-7 object-contain flex-shrink-0" />
                            <span className="text-white text-xs font-medium capitalize">{p.name}</span>
                          </button>
                        ))
                      ) : (
                        <p className="text-zinc-400 text-xs px-3 py-2.5">No matches — try another name.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Text fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-zinc-300 text-xs font-semibold block mb-1.5">Card name</label>
                    <input
                      type="text"
                      maxLength={20}
                      value={builderCardName}
                      onChange={(e) => setBuilderCardName(e.target.value)}
                      placeholder="e.g. Pikachu"
                      className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-300 text-xs font-semibold block mb-1.5">HP</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={3}
                      value={builderHp}
                      onChange={(e) => setBuilderHp(e.target.value.replace(/[^0-9]/g, ''))}
                      className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-300 text-xs font-semibold block mb-1.5">Type</label>
                    <select
                      value={builderType}
                      onChange={(e) => setBuilderType(e.target.value)}
                      className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white text-sm"
                    >
                      {cardTypes.map(t => <option key={t.id} value={t.id}>{t.id}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="text-zinc-300 text-xs font-semibold block mb-1.5">Move name</label>
                    <input
                      type="text"
                      maxLength={24}
                      value={builderMoveName}
                      onChange={(e) => setBuilderMoveName(e.target.value)}
                      placeholder="e.g. Thunderbolt"
                      className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="text-zinc-300 text-xs font-semibold block mb-1.5">Move description</label>
                    <textarea
                      maxLength={90}
                      rows={2}
                      value={builderMoveDesc}
                      onChange={(e) => setBuilderMoveDesc(e.target.value)}
                      placeholder="e.g. Deals 90 damage to the opposing Pokémon."
                      className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-sm resize-none"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-300 text-xs font-semibold block mb-1.5">Damage</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={3}
                      value={builderDamage}
                      onChange={(e) => setBuilderDamage(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder={String(builderAutoDamage)}
                      className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-300 text-xs font-semibold block mb-1.5">Card number</label>
                    <input
                      type="text"
                      maxLength={10}
                      value={builderCardNumberInput}
                      onChange={(e) => setBuilderCardNumberInput(e.target.value)}
                      placeholder={builderAutoCardNumber}
                      className="px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-sm"
                    />
                  </div>
                </div>

                {/* Design code */}
                <div className="bg-zinc-900/50 border border-zinc-700/70 rounded-xl p-3.5">
                  <p className="text-zinc-300 text-xs font-semibold mb-2">Your design code</p>
                  {builderCode ? (
                    <>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-black/70 border border-zinc-700/70 rounded-lg px-2.5 py-2 text-cyan-300 text-xs break-all">{builderCode}</code>
                        <button
                          onClick={handleCopyBuilderCode}
                          className="flex items-center justify-center flex-shrink-0 rounded-lg transition-all duration-150 active:scale-95"
                          style={{width:'36px', height:'36px', background: builderCodeCopied ? 'linear-gradient(135deg,#22c55e,#16a34a)' : 'linear-gradient(135deg,#04aff7,#f42ae4)'}}
                          title="Copy code"
                        >
                          {builderCodeCopied ? <Check className="h-4 w-4 text-white" /> : <Copy className="h-4 w-4 text-white" />}
                        </button>
                      </div>
                      <p className="text-zinc-400 text-[10px] mt-2 leading-snug">This code always rebuilds this exact design — send it to CHEEKS to have it made live on Whatnot.</p>
                    </>
                  ) : (
                    <p className="text-zinc-400 text-xs">Pick a Pokémon and enter a card name to generate your code.</p>
                  )}
                </div>

                {/* Load a code */}
                <div className="bg-zinc-900/50 border border-zinc-700/70 rounded-xl p-3.5">
                  <p className="text-zinc-300 text-xs font-semibold mb-2">Already have a code?</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={builderLoadCodeInput}
                      onChange={(e) => { setBuilderLoadCodeInput(e.target.value); setBuilderLoadCodeError(''); }}
                      placeholder="Paste your design code…"
                      className="flex-1 px-3 py-2 bg-zinc-800/50 border border-zinc-700 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent w-full text-white placeholder-zinc-500 text-xs"
                    />
                    <button
                      onClick={handleLoadBuilderCode}
                      disabled={!builderLoadCodeInput.trim()}
                      className="flex-shrink-0 px-3.5 py-2 rounded-lg text-white text-xs font-semibold transition-all duration-150 active:scale-95 disabled:opacity-40"
                      style={{background:'linear-gradient(135deg,#04aff7,#f42ae4)'}}
                    >
                      Load
                    </button>
                  </div>
                  {builderLoadCodeError && (
                    <p className="text-red-400 text-[10px] mt-2">{builderLoadCodeError}</p>
                  )}
                </div>

              </div>
            </div>
          </div>
          )}
        </main>
      )}

      {activeTab === 'arcade' && (
        <main className="flex-1">
          <div className="max-w-2xl mx-auto px-4 pt-4 pb-6">
            <div className="flex flex-col gap-4">
              {GAMES_LIST.map((g) => g.petPanel ? (
                // Tamagotcheeks: a live view of the server Togepi, a sleeker version of the Discord panel
                <div key={g.id} className="bg-gradient-to-br from-zinc-800/85 to-black/90 border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] overflow-hidden w-full flex flex-col">
                  {(() => {
                    const p = togepiPet;
                    const name = p ? (p.nickname ? `${p.nickname} the ${p.species}` : p.species) : 'Togepi';
                    const stats = [
                      { key: 'food',      label: 'Food',   emoji: '🍖', color: '#f59e0b' },
                      { key: 'happiness', label: 'Happy',  emoji: '💛', color: '#fbe510' },
                      { key: 'energy',    label: 'Energy', emoji: '⚡', color: '#04aff7' },
                      { key: 'clean',     label: 'Clean',  emoji: '🛁', color: '#a78bfa' },
                    ];
                    const evo = p && p.evolveAt ? Math.min(100, Math.round((p.xp / p.evolveAt) * 100)) : 100;
                    const mins = p?.updatedAt ? Math.max(0, Math.floor((Date.now() - p.updatedAt) / 60000)) : null;
                    return (
                      <>
                        <div className="px-4 pt-3.5 pb-3 flex flex-col gap-3">
                          {/* Header: name and level, awake/asleep in the corner, mood underneath */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              {/* Name, level and awake/asleep together (the pill wraps underneath on very narrow phones) */}
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <p className="text-white font-bold text-base leading-tight whitespace-nowrap">{name}</p>
                                {p?.level ? (
                                  <span className="flex-shrink-0 rounded-md px-1.5 py-0.5 font-bold text-white" style={{ fontSize: '0.6rem', lineHeight: 1.2, background: 'linear-gradient(135deg,#04aff7,#f42ae4)' }}>Lv. {p.level}</span>
                                ) : null}
                                {p && (
                                  <span className="flex-shrink-0 rounded-full px-2 py-0.5 font-semibold" style={{ fontSize: '0.6rem', lineHeight: 1.2, background: p.asleep ? 'rgba(167,139,250,0.15)' : 'rgba(35,165,90,0.15)', color: p.asleep ? '#a78bfa' : '#23a55a', border: `1px solid ${p.asleep ? 'rgba(167,139,250,0.4)' : 'rgba(35,165,90,0.4)'}` }}>
                                    {p.asleep ? '💤 Asleep' : '● Awake'}
                                  </span>
                                )}
                              </div>
                              <p className="text-zinc-400 mt-1" style={{ fontSize: '0.68rem', lineHeight: 1.3 }}>{p ? p.mood : g.tagline}</p>
                            </div>
                            {p?.totalCare > 0 && (
                              // Community total in the corner: every Feed, Play, Pet, Bath and Nap from server members
                              <div className="flex-shrink-0 flex flex-col items-end gap-1">
                                <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold whitespace-nowrap" style={{ fontSize: '0.6rem', lineHeight: 1.2, background: 'rgba(251,229,16,0.12)', color: '#fbe510', border: '1px solid rgba(251,229,16,0.35)' }}>
                                  💛 {p.totalCare.toLocaleString()} acts of care
                                </span>                              </div>
                            )}
                          </div>

                          {/* Evolution: a stepper. Done stages are ticked, the current one glows, the next is a white silhouette,
                              and the line from the current stage to the next one fills up with XP */}
                          {p && (() => {
                            const chain = [{ species: 'Togepi', dex: 175 }, { species: 'Togetic', dex: 176 }, { species: 'Togekiss', dex: 468 }];
                            const current = Math.max(0, chain.findIndex(c => c.species === p.species));
                            const next = chain[current + 1];
                            const fill = (i) => (i <= current ? 100 : i === current + 1 ? evo : 0); // the line leading into stage i
                            return (
                              <div className="rounded-xl px-3 pt-2.5 pb-3 flex flex-col gap-2.5" style={{ background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(63,63,70,0.7)', boxShadow: 'inset 0 1px 8px rgba(0,0,0,0.6)' }}>
                                <div className="flex items-center justify-between" style={{ fontSize: '0.62rem', lineHeight: 1 }}>
                                  <span className="text-zinc-300 font-medium">✨ Evolution</span>
                                  <span className="text-white font-semibold">
                                    {next ? `${(p.xp || 0).toLocaleString()} / ${p.evolveAt.toLocaleString()} XP` : `${(p.xp || 0).toLocaleString()} XP · Fully evolved!`}
                                  </span>
                                </div>
                                <div className="flex items-start">
                                  {chain.map((c, i) => (
                                    <React.Fragment key={c.dex}>
                                      {i > 0 && (
                                        <div className="flex-1 flex items-center px-1.5" style={{ height: '40px' }}>
                                          <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(63,63,70,0.6)' }}>
                                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${fill(i)}%`, background: 'linear-gradient(90deg,#04aff7,#f42ae4)' }} />
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex flex-col items-center gap-1 flex-shrink-0" style={{ width: '48px' }}>
                                        <div
                                          className="relative flex items-center justify-center rounded-xl"
                                          style={{
                                            width: '40px', height: '40px',
                                            background: i === current ? 'rgba(244,42,228,0.12)' : 'rgba(39,39,42,0.6)',
                                            border: `1px solid ${i === current ? 'rgba(244,42,228,0.6)' : 'rgba(63,63,70,0.7)'}`,
                                            boxShadow: i === current ? '0 0 12px rgba(244,42,228,0.35)' : 'none',
                                          }}
                                        >
                                          <img
                                            src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/showdown/${c.dex}.gif`}
                                            alt={c.species}
                                            className="max-w-full max-h-full"
                                            style={{
                                              imageRendering: 'pixelated',
                                              // Not reached yet: a white silhouette, so it shows up against the dark box
                                              filter: i > current ? 'brightness(0) invert(1)' : 'none',
                                              opacity: i > current ? 0.6 : i < current ? 0.8 : 1,
                                            }}
                                          />
                                          {i < current && (
                                            <span className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full" style={{ width: '14px', height: '14px', background: '#23a55a', border: '2px solid #09090b' }}>
                                              <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            </span>
                                          )}
                                        </div>
                                        <span className="font-semibold" style={{ fontSize: '0.58rem', lineHeight: 1, color: i === current ? '#ffffff' : i < current ? '#a1a1aa' : '#71717a' }}>{c.species}</span>
                                      </div>
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}

                          {p && (
                            <>
                              {/* Stat bars */}
                              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                                {stats.map(st => {
                                  const v = Math.max(0, Math.min(100, Math.round(p[st.key] ?? 0)));
                                  const low = v < 30;
                                  return (
                                    <div key={st.key} className="flex flex-col gap-1">
                                      <div className="flex items-center justify-between" style={{ fontSize: '0.62rem', lineHeight: 1 }}>
                                        <span className="text-zinc-300 font-medium">{st.emoji} {st.label}</span>
                                        <span className="font-semibold" style={{ color: low ? '#f87171' : '#ffffff' }}>{v}%</span>
                                      </div>
                                      <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(63,63,70,0.6)' }}>
                                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${v}%`, background: low ? '#ef4444' : st.color }} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>

                        {/* Footer: how it works and how to take part (matches the togepi cog's rules) */}
                        <div className="px-4 py-3 relative edge-glow-top-thin edge-glow-bold">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-baseline gap-2 min-w-0">
                              <p className="text-white font-bold text-sm">{g.label}</p>
                              {mins !== null && (
                                <span className="text-zinc-500 truncate" style={{ fontSize: '0.6rem' }}>updated {mins < 1 ? 'just now' : `${mins}m ago`}</span>
                              )}
                            </div>
                            {/* Opens the pet's channel in the Discord server */}
                            <a
                              href="https://discord.com/channels/1466528743265931459/1561583135580233748"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="relative z-30 flex-shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold transition-all active:scale-95 hover:brightness-110"
                              style={{ fontSize: '0.65rem', lineHeight: 1.2, color: '#ffffff', background: '#5865F2', boxShadow: '0 2px 12px rgba(88,101,242,0.35)' }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" /></svg>
                              Play on Discord
                            </a>
                          </div>
                          <p className="text-zinc-400 text-[11px] mt-0.5" style={{ lineHeight: 1.45 }}>
                            A community pet that the whole server raises together. Its stats drop over time, so it needs regular care from everyone.
                          </p>
                          <ul className="mt-2 flex flex-col gap-1 text-zinc-300" style={{ fontSize: '0.65rem', lineHeight: 1.4 }}>
                            <li>🎮 <span className="text-white font-semibold">Take part:</span> join our Discord and tap 🍖 Feed, 🎾 Play, 🤗 Pet, 🛁 Bath or 💤 Nap on the pet's panel. Each button has a short cooldown, so check back often.</li>
                            <li>⭐ <span className="text-white font-semibold">Earn XP:</span> every act of care adds XP, with bonus XP for helping when a bar is low (under 30%).</li>
                            <li>✨ <span className="text-white font-semibold">Evolve:</span> Togepi becomes Togetic at 1,000 XP and Togekiss at 5,000 XP, but only when it's at least 80% happy.</li>
                            <li>🌙 <span className="text-white font-semibold">Bedtime:</span> it sleeps 23:00–07:00 UK time.</li>
                          </ul>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <button
                  key={g.id}
                  onClick={() => { if (!g.comingSoon) switchTab(g.id); }}
                  disabled={g.comingSoon}
                  className={`${g.minimal ? 'bg-transparent' : 'bg-gradient-to-br from-zinc-800/85 to-black/90'} border border-zinc-700/70 rounded-2xl corner-glow shadow-[0_6px_24px_rgba(0,0,0,0.5)] overflow-hidden transition-all duration-150 text-left w-full flex flex-col ${g.comingSoon ? 'cursor-not-allowed' : 'active:scale-[0.98]'}`}
                >
                  {!g.minimal && (
                    <div className="relative w-full flex items-center justify-center glow-neutral overflow-hidden" style={{ height: g.banner ? 'auto' : '120px', background: g.banner ? '#000000' : g.gradient }}>
                      {g.banner ? (
                        <img src={g.banner} alt={g.label} className="w-full h-auto block" />
                      ) : (
                        <span style={{ fontSize: '2.75rem' }}>{g.emoji}</span>
                      )}
                      {g.id === 'toge-dash' && togeDashLeaders.length > 0 && (
                        <div className="absolute top-2 right-2 flex flex-col gap-1 rounded-lg px-2 py-1.5" style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}>
                          <style>{`
                            @keyframes trophyBounce { 0%, 100% { transform: translateY(0) rotate(0deg); } 25% { transform: translateY(-2px) rotate(-8deg); } 50% { transform: translateY(0) rotate(0deg); } 75% { transform: translateY(-2px) rotate(8deg); } }
                            .trophy-bounce { display: inline-block; animation: trophyBounce 1.4s ease-in-out infinite; }
                          `}</style>
                          <div className="flex items-center gap-1 mb-0.5">
                            <span className="trophy-bounce" style={{ fontSize: '10px' }}>🏆</span>
                            <span className="text-white text-[9px] font-bold uppercase tracking-wider">Leaderboard</span>
                            <span className="text-zinc-400 text-[9px] font-bold uppercase tracking-wider">Top {togeDashLeaders.length}</span>
                          </div>
                          {togeDashLeaders.map((entry, i) => (
                            <div key={entry.uid} className="flex items-center gap-1.5">
                              <span className="text-zinc-400 font-mono text-[9px] w-3 flex-shrink-0 text-center">{i + 1}</span>
                              <span className="text-white text-[9px] font-medium flex-1">{entry.username.length > 15 ? entry.username.slice(0, 15) + '…' : entry.username}</span>
                              <span className="text-white text-[9px] font-semibold font-mono flex-shrink-0 text-right ml-2">{entry.score}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="px-4 py-3 flex items-center justify-between gap-3 relative edge-glow-top-thin edge-glow-bold">
                    <div>
                      <p className="text-white font-bold text-sm">{g.label}</p>
                      <p className="text-zinc-400 text-[11px] mt-0.5">{g.tagline}</p>
                    </div>
                    {g.comingSoon ? (
                      <span className="text-zinc-400 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border border-zinc-700/70 flex-shrink-0">Soon</span>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#04aff7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><polyline points="9 18 15 12 9 6"/></svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </main>
      )}

      {activeTab === 'battle' && (
        <main className="flex-1 flex flex-col">
          <div className="flex-1 flex flex-col">
            <BattleArena
              database={database}
              discordUser={discordUser}
              onLogin={handleDiscordLogin}
            />
          </div>
        </main>
      )}

      {activeTab === 'toge-dash' && (
        <main className="flex-1 flex flex-col">
          <div className="flex-1 flex flex-col">
            <PokeDash
              database={database}
              discordUser={discordUser}
              onLogin={handleDiscordLogin}
            />
          </div>
        </main>
      )}

      {/* ── INVENTORY LIGHTBOX ── */}
      {selectedInventoryImage && (
        <div
          className="fixed inset-0 z-[400] flex items-center justify-center p-4"
          style={{background:'rgba(0,0,0,0.85)', backdropFilter:'blur(8px)', animation:'fadeInBackdrop 0.25s ease forwards'}}
          onClick={() => setSelectedInventoryImage(null)}
        >
          <style>{`
            @keyframes fadeInBackdrop {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
            @keyframes popInCard {
              from { opacity: 0; transform: scale(0.92) translateY(16px); }
              to   { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
          <div
            className="relative w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl border border-zinc-700/70"
            style={{background:'#000000', animation:'popInCard 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards'}}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedInventoryImage(null)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
              style={{background:'rgba(0,0,0,0.9)', border:'1px solid rgba(0,0,0,0.75)'}}
            >
              <X className="h-4 w-4" />
            </button>

            {/* Image */}
            <div className="relative w-full" style={{aspectRatio:'1/1'}}>
              <img
                src={`/${selectedInventoryImage.folder}/${selectedInventoryImage.filename}`}
                alt={selectedInventoryImage.name}
                className="w-full h-full object-contain"
              />
              {/* Grade badge */}
              {selectedInventoryImage.grade && (
                <div className="absolute top-3 left-3 bg-gradient-to-r from-cyan-500 to-pink-600 text-white px-2.5 py-1 rounded-md text-xs font-bold shadow-lg border border-cyan-400/50">
                  {selectedInventoryImage.grade}
                </div>
              )}
              {selectedInventoryImage.serial && (
                <div className="absolute bottom-3 left-3 text-white font-bold tracking-widest shadow-lg" style={{fontSize:'0.7rem', background:'rgba(0,0,0,0.4)', backdropFilter:'blur(4px)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:'6px', padding:'3px 8px', letterSpacing:'0.1em'}}>
                  {selectedInventoryImage.serial}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="px-4 py-3 border-t border-zinc-700/70 flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-sm leading-tight">{selectedInventoryImage.name}</h3>
                {selectedInventoryImage.set.split('  ').map((line, idx) => (
                  <p key={idx} className="text-zinc-400 text-xs mt-0.5 leading-tight">{line}</p>
                ))}
              </div>
              {selectedInventoryImage.filename.includes('Crystal Palace') && (
                <img src="/cpfc.png" alt="CPFC" style={{width:'52px', height:'52px', objectFit:'contain', flexShrink:0}} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <footer className="bg-black/80 backdrop-blur-lg edge-glow-top relative">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center justify-center gap-3">
              <a href="https://www.whatnot.com/user/togecheeks/shows" target="_blank" rel="noopener noreferrer" className="group">
                <img src="/wnlogo.png" alt="Whatnot" className="h-6 w-6 object-contain opacity-100 group-hover:opacity-100 transition-opacity duration-200" />
              </a>
              <a href="https://cheeks.uk/docs" className="group">
                <img src="/discord.png" alt="Discord" className="h-6 w-6 object-contain opacity-100 group-hover:opacity-100 transition-opacity duration-200" />
              </a>
              <a href="https://www.instagram.com/togecheeks" target="_blank" rel="noopener noreferrer" className="group">
                <img src="/instagram.png" alt="Instagram" className="h-6 w-6 object-contain opacity-100 group-hover:opacity-100 transition-opacity duration-200" />
              </a>
            </div>
            <span className="text-white text-[10px]">© 2026 Cheeks × Cheeky Cards</span>
          </div>
        </div>
      </footer>
      </>
      )}

      {/* ── POKÉMON GO MODAL ── */}
      {showPogoModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{background:'rgba(0,0,0,0.7)', backdropFilter:'blur(4px)'}} onClick={() => setShowPogoModal(false)}>
          <div className="bg-zinc-900 border border-zinc-700/70 rounded-2xl shadow-2xl w-full max-w-xs p-5" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <img src="/pogo.png" alt="Pokémon GO" className="w-7 h-7 object-contain" />
                <span className="text-white font-bold text-base">Pokémon GO</span>
              </div>
              <button onClick={() => setShowPogoModal(false)} className="text-zinc-400 hover:text-white transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Trainer name — centred title */}
            <div className="flex items-center justify-center gap-2 mb-1">
              <img src="/valor.webp" alt="Team Valor" className="object-contain flex-shrink-0" style={{width:'26px', height:'26px'}} />
              <span className="text-white font-bold text-base">togecheeks</span>
              {/* Level badge, Pokémon GO style: gradient ring around an opposite-gradient disc */}
              <span
                aria-label="Level 71"
                className="flex items-center justify-center rounded-full flex-shrink-0"
                style={{width:'26px', height:'26px', padding:'5px', background:'linear-gradient(to bottom, #bae6fd, #2563eb)', boxShadow:'0 0 0 1px #ffffff, 0 1px 4px rgba(0,0,0,0.5)'}}
              >
                <span
                  className="flex items-center justify-center rounded-full w-full h-full font-semibold font-mono"
                  style={{background:'#ffffff', color:'#1d4ed8', fontSize:'0.7rem', lineHeight:1, letterSpacing:'-0.04em'}}
                >
                  71
                </span>
              </span>
            </div>
            {/* Trainer code box */}
            <div className="bg-black/60 rounded-xl border border-zinc-700/70 p-3 mb-3 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400 font-medium" style={{fontSize:'0.7rem'}}>Trainer Code</span>
                <div className="relative flex items-center" style={{height:'1rem'}}>
                  <span className={`absolute right-0 flex items-center gap-1 transition-all duration-500 ${showCopiedToast ? 'opacity-100' : 'opacity-0'}`}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    <span className="text-green-400 font-semibold whitespace-nowrap" style={{fontSize:'0.7rem'}}>Trainer Code Copied</span>
                  </span>
                  <span className={`transition-all duration-500 ${showCopiedToast ? 'opacity-0' : 'opacity-100'} text-white font-semibold font-mono whitespace-nowrap`} style={{fontSize:'0.7rem'}}>6644 6392 9998</span>
                </div>
              </div>
            </div>
            {/* QR code */}
            {/* Same box style as the trainer code; the QR background (0a0a0b) is what bg-black/60 looks like on the zinc-900 modal, so it blends in */}
            <div className="flex flex-col items-center gap-2">
              <div className="bg-black/60 rounded-xl border border-zinc-700/70 p-3">
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=664463929998&bgcolor=0a0a0b&color=ffffff&margin=0`}
                  alt="QR Code"
                  className="w-40 h-40 block"
                />
              </div>
            </div>
            {/* Link to the Pokémon GO: South London section in the docs */}
            <a
              href="https://cheeks.uk/docs/#/?id=pok%c3%a9mon-go-south-londonpok%c3%a9mon-go-sl-s%ce%9erv%ce%9er-l%ce%9egacy"
              className="mt-3 flex items-center justify-between gap-2 bg-black/60 rounded-xl border border-zinc-700/70 px-3 py-2.5 hover:border-zinc-500/70 active:scale-[0.98] transition-all duration-200"
            >
              <span className="flex items-center gap-1.5 text-white font-semibold" style={{fontSize:'0.7rem'}}>
                <img src="https://cheeks.uk/docs/SL_logo.png" alt="Pokémon GO: South London logo" loading="lazy" className="flex-shrink-0 object-contain rounded-md" style={{width:'22px', height:'22px'}} />
                Pokémon GO: South London
                <img src="https://cheeks.uk/docs/community.png" alt="" className="flex-shrink-0" style={{width:'1.2em', height:'1.2em'}} />
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#a1a1aa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><polyline points="9 18 15 12 9 6"/></svg>
            </a>
          </div>
        </div>
      )}

    </div>
  );
};

export default CardExplorer;
